/**
 * Shared types for schema compare operations.
 * Used by both renderer (React) and main (Electron) processes.
 *
 * Import from Electron via relative path:
 * import type { ... } from '../../src/features/workspace/schema-compare/types/schemaCompare';
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

// =============================================================================
// Schema Compare Endpoint (Source/Target Selection)
// =============================================================================

/**
 * Identifies a database endpoint for schema comparison.
 */
export type SchemaCompareEndpoint = {
  /** Connection ID for this endpoint */
  connectionId: string;
  /** Database name to compare */
  database: string;
};

// =============================================================================
// Schema Compare Options
// =============================================================================

/**
 * Options controlling schema comparison behavior.
 */
export type SchemaCompareOptionsDto = {
  /** Ignore case differences in identifiers */
  ignoreCase?: boolean;
  /** Ignore whitespace in view/routine definitions */
  ignoreWhitespace?: boolean;
  /**
   * Format view/routine/trigger DDL with the engine-aware SQL formatter
   * before classification. Whitespace + keyword-case drift collapses to
   * "identical". Default true. When false, falls back to char-by-char
   * comparison on the raw DDL (subject to `ignoreWhitespace`).
   */
  formatSqlForComparison?: boolean;
  /** Include tables in comparison */
  includeTables?: boolean;
  /** Include views in comparison */
  includeViews?: boolean;
  /** Include stored procedures in comparison */
  includeProcedures?: boolean;
  /** Include functions in comparison */
  includeFunctions?: boolean;
  /** Include sequences in comparison (SQL Server, PostgreSQL, Oracle) */
  includeSequences?: boolean;
  /**
   * Include user-defined types in comparison (SQL Server only — table types,
   * alias types and CLR types; S7). Default true. Ignored by engines whose
   * fetch provider has no `fetchUserTypes`, and hidden in the UI for them —
   * see `ENGINE_OBJECT_TYPES` in `utils/compareDepth.ts`.
   */
  includeUserTypes?: boolean;
  /** When true, do not auto-exclude per-engine system schemas (sys, INFORMATION_SCHEMA, …). Default false. */
  includeSystemSchemas?: boolean;
  /** Property keys to suppress when comparing (e.g. fillFactor, autoIncrement). */
  ignoredProperties?: string[];
  /**
   * Soporte 9 F1 — "Ignore object owner". When true, the schema qualifier in
   * a view/procedure/function/trigger definition's CREATE header carries no
   * verdict weight: `CREATE PROCEDURE [dbo].[x]` and `CREATE PROCEDURE [x]`
   * for the same catalog object compare identical (MSSQL stores module text
   * verbatim as typed, so qualification style floods the diff when the two
   * databases' objects were created differently — Apex SQL Diff's "Ignore
   * owner"). Header only — a qualifier in the body is a real difference and
   * keeps its weight. Default false. Compare-time only: no fetch reads it
   * (rediff-eligible) and no generated script is affected — reconciled pairs
   * are simply `identical` and never scripted.
   */
  ignoreOwner?: boolean;
  /**
   * When true, fetch full per-table metadata: indexes, foreign keys, triggers,
   * engine-specific options, partition definition, column extras. When false,
   * only column-level diffs are produced (faster). Default true.
   */
  detailed?: boolean;
  // NO `wrapScriptInTransaction` HERE, on purpose (R3-2). The transaction wrap
  // is a SCRIPT-GENERATION input, not a compare input: the generate-script IPC
  // carries it as its own `wrapInTransaction` argument, and nothing in a fetch
  // or a diff reads it. It rides in the tab-persisted `SchemaCompareOptions`
  // (`workspaceTypes.ts`) so it can be exported/loaded/saved-as-default with
  // the rest of the profile — but adding it to THIS type would put it inside
  // `schemaCompareOptionsEqual`'s `BOOLEAN_OPTION_DEFAULTS` (a wrap toggle
  // would then read as "the options changed since the last run" and route the
  // next Compare through the cache, serving a stale diff) and inside
  // `deriveFetchShape`'s scope. See the classification gate in
  // `utils/schemaCompareOptionsEqual.ts`.
};

/**
 * Default volatile properties pre-checked in the compare-options dialog.
 * These rarely indicate a meaningful schema difference and are ignored by default.
 */
export const DEFAULT_IGNORED_PROPERTIES: string[] = [
  'fillFactor',
  'autoIncrement',
  'pctFree',
  'iniTrans',
  'identitySeed',
  'identityIncrement',
];

/**
 * User-defined schema filter applied per endpoint.
 * - 'include' compares only listed schemas
 * - 'exclude' compares everything except listed schemas
 * Empty `schemas` array is treated as "no filter" — same as omitting the field.
 */
export type SchemaFilter = {
  mode: 'include' | 'exclude';
  schemas: string[];
};

// =============================================================================
// Schema Compare IPC Types (for future slices)
// =============================================================================

/**
 * Raw provider catalog lists (schema+name only, as returned directly by a
 * `SchemaFetchProvider`, BEFORE `applySchemaFilters`/single-object narrowing)
 * for a DB endpoint. Lets `fetchSchemaSnapshot` reuse a list the caller
 * already enumerated instead of calling `provider.fetchTableList`/
 * `fetchViewList`/`fetchRoutineList` again for the same connection+database.
 *
 * Populated ONLY by the main-process F16 object-count probe
 * (`computeEndpointObjectCount` in `ipcHandlers.ts`) immediately before the
 * real fetch, on the SAME connection+database+options within a single
 * `schema-compare-run` invocation — no cross-request staleness risk. Never
 * set by a renderer caller of the standalone `schema-compare-fetch-snapshot`
 * IPC; absent there, the fetch just enumerates as before.
 */
export type SchemaObjectRawLists = {
  tables?: Array<{ name: string; schema: string; id?: string }>;
  views?: Array<{ name: string; schema: string; id?: string }>;
  routines?: Array<{ name: string; schema: string; routineType: 'procedure' | 'function'; id?: string }>;
  sequences?: Array<{ name: string; schema: string }>;
  /**
   * S7 — the WHOLE user-type snapshots, not just names. `fetchUserTypes` is a
   * single set-based read (a handful of catalog queries for the whole
   * database, never one per type), so the probe and the fetch would otherwise
   * run the identical queries twice. Filtering (schema filter, single-object
   * narrowing) is applied by the fetcher on top, exactly as for the lists.
   * The WHOLE fetch result rides here (not just the snapshots) so a partial
   * catalog failure the probe saw is disclosed by the fetch that reuses it.
   */
  userTypes?: SchemaUserTypeFetchResult;
};

/**
 * Request to fetch a schema snapshot from a database.
 * Will be used by the Schema Fetching Engine slice.
 */
export type FetchSchemaSnapshotRequest = {
  connectionId: string;
  database: string;
  options?: SchemaCompareOptionsDto;
  operationId: string;
  /**
   * F08 — the run-level base operation id `schema-compare-run` registers for
   * its whole lifetime (including the F16 object-count probe and folder-read
   * steps that happen BEFORE this per-side fetch even starts, and thus before
   * `operationId` above is registered). When set, `fetchSchemaSnapshot`'s
   * cancellation check consults this id in addition to its own, so a cancel
   * that arrived during those earlier steps is honoured on the very first
   * check here instead of being silently swallowed until the whole fetch
   * completes.
   */
  parentOperationId?: string;
  /** Single object selection (when fetching only one object instead of entire database) */
  singleObject?: SchemaCompareObjectSelection;
  /** Optional user-defined schema filter (per-endpoint). Ignored when singleObject is set. */
  schemaFilter?: SchemaFilter;
  /**
   * Database Blueprint folder source. When set, the fetcher reads the folder's
   * `.jamsql/snapshot.json` sidecar (reconciled against on-disk DDL) instead
   * of touching the live database. `connectionId` / `database` should still
   * be supplied (synthetic is fine) — they're carried through for display.
   */
  folderSource?: {
    folderPath: string;
    linkLabel?: string;
  };
  /**
   * Reuse a catalog list already enumerated for THIS same connection+database
   * (see `SchemaObjectRawLists`) instead of re-enumerating it. A category
   * absent here (or the whole field absent) enumerates normally — this is a
   * pure optimisation, never a behavior change (W4.2 fix round 1 / F09).
   */
  prefetchedLists?: SchemaObjectRawLists;
};

/**
 * Progress event during schema fetching.
 *
 * `objectSchema` and `objectName` are populated for per-object phases
 * (`tables`, `views`, `procedures`, `functions`, `sequences`, `userTypes`) so
 * consumers can surface a live "fetching X" line. They're absent on `complete`.
 */
export type SchemaFetchProgress = {
  operationId: string;
  phase: 'tables' | 'views' | 'procedures' | 'functions' | 'sequences' | 'userTypes' | 'complete';
  current: number;
  total: number;
  objectSchema?: string;
  objectName?: string;
  /**
   * F07 — which endpoint this event belongs to. `fetchSchemaSnapshot` itself
   * has no notion of "source" vs "target" (it just fetches ONE database), so
   * this is populated by the caller relaying the event onward — currently
   * only `schema-compare-run`'s two `fetchSchemaSnapshot` calls in
   * `ipcHandlers.ts`, which is the one place that runs a fetch twice (once
   * per side) under a single operation id. Optional so every other consumer
   * of this type (Database Blueprint's single-sided fetch progress, the MCP
   * schema-compare tool, which composes its own "Fetching source: …" text
   * without this field) keeps compiling unchanged.
   */
  side?: 'source' | 'target';
};

/**
 * Request to cancel a schema compare operation.
 */
export type CancelSchemaOperationRequest = {
  operationId: string;
};

// =============================================================================
// Schema Snapshot Model (Engine-Agnostic)
// =============================================================================

// =============================================================================
// Engine-specific table options (discriminated by `engine`)
// =============================================================================

export type MssqlTableOptions = {
  engine: 'mssql';
  dataCompression?: 'NONE' | 'ROW' | 'PAGE' | 'COLUMNSTORE';
  fillFactor?: number;
  memoryOptimized?: boolean;
  durability?: 'SCHEMA_AND_DATA' | 'SCHEMA_ONLY';
};

export type MysqlTableOptions = {
  engine: 'mysql';
  storageEngine?: string;
  rowFormat?: string;
  defaultCharset?: string;
  defaultCollation?: string;
  autoIncrement?: number;
};

export type PgsqlTableOptions = {
  engine: 'pgsql';
  tablespace?: string;
  fillFactor?: number;
  inheritsFrom?: string[];
  unlogged?: boolean;
};

export type OracleTableOptions = {
  engine: 'oracle';
  tablespace?: string;
  pctFree?: number;
  pctUsed?: number;
  iniTrans?: number;
  compression?: string;
  logging?: boolean;
  organization?: 'HEAP' | 'INDEX' | 'EXTERNAL';
};

export type SqliteTableOptions = {
  engine: 'sqlite';
  withoutRowid?: boolean;
  strict?: boolean;
};

export type EngineSpecificTableOptions =
  | MssqlTableOptions
  | MysqlTableOptions
  | PgsqlTableOptions
  | OracleTableOptions
  | SqliteTableOptions;

// NOTE: per-partition bound values (PG `FOR VALUES`, MSSQL partition boundary list,
// Oracle `VALUES LESS THAN`, MySQL partition value lists) are deliberately not captured.
// Definition-level only for v1; bound-level diffing is a separate enrichment.
export type SchemaPartitionSnapshot = {
  strategy: 'RANGE' | 'LIST' | 'HASH' | 'COMPOSITE';
  columns: string[];
  schemeName?: string;
  expression?: string;
  subStrategy?: 'RANGE' | 'LIST' | 'HASH';
};

/**
 * Column metadata within a table snapshot.
 */
export type SchemaColumnSnapshot = {
  name: string;
  dataType: string;
  /**
   * The resolved BASE engine type when `dataType` is a user-defined ALIAS type
   * (e.g. a SQL Server `CREATE TYPE dbo.Name FROM nvarchar(50)` reports
   * `dataType: 'Name'` here). Cross-engine type mapping uses `baseDataType`
   * (the portable base, `nvarchar`) so an alias still maps to a canonical type
   * instead of falling through to "no canonical mapping". DISPLAY surfaces
   * (Object Explorer, Schema Compare DDL) keep using `dataType` so the alias
   * name is preserved. Unset for engines/columns with no alias indirection.
   */
  baseDataType?: string;
  maxLength?: number | null;
  precision?: number | null;
  scale?: number | null;
  isNullable: boolean;
  isIdentity?: boolean;
  /**
   * PostgreSQL `GENERATED { ALWAYS | BY DEFAULT } AS IDENTITY` kind.
   * EMISSION-ONLY (NOT part of the compared-field set in columnChangesDetector —
   * the diff stays keyed on `isIdentity`): introspection does not report the
   * kind, so comparing it would flag every parsed identity column as modified
   * forever. Populated by the DDL parse layer (Database Blueprint folders,
   * Table Designer definitions) so generators emit the right identity clause;
   * the Table Designer compares two parsed definitions and scripts a kind
   * change itself. Unset everywhere else.
   *
   * `'BY DEFAULT ON NULL'` is Oracle-only (`GENERATED BY DEFAULT ON NULL AS
   * IDENTITY` — an explicit NULL is replaced by the next sequence value instead
   * of being rejected). It is a THIRD kind, not a flag on `BY DEFAULT`: dropping
   * `ON NULL` narrows what the column accepts, so the value has to survive to
   * the emitter rather than degrade to `BY DEFAULT`. PostgreSQL's grammar has no
   * such form and its reader never produces it, so the PG-only consumers
   * (`generateAlterColumn`'s `ADD GENERATED …`, the Designer's
   * `planIdentityChanges`) are unreachable with it.
   */
  identityGeneration?: 'ALWAYS' | 'BY DEFAULT' | 'BY DEFAULT ON NULL';
  /**
   * PostgreSQL identity sequence options other than START / INCREMENT (which
   * are `identitySeed` / `identityIncrement`), as one canonical clause —
   * `MINVALUE 1 MAXVALUE 1000 CACHE 20 NO CYCLE` (see
   * `electron/schema-compare/utils/pgIdentityOptions.ts`). EMISSION-ONLY,
   * populated by the DDL parse layer: an ADD restates it verbatim and the
   * Table Designer scripts (`SET CACHE …`) or refuses a change to it.
   */
  identityOptions?: string;
  defaultValue?: string | null;
  /**
   * EMISSION-ONLY metadata (NOT part of the compared-field set in
   * columnChangesDetector — the diff stays keyed on `defaultValue`).
   * `false` = the default was a quoted STRING LITERAL, so a script generator
   * that emits the default must re-quote it as a SQL string literal. `true` or
   * unset = a number, keyword (e.g. CURRENT_TIMESTAMP), function call, or
   * expression — emit the `defaultValue` text verbatim. Populated by the
   * Database Blueprint DDL parse layer (folder→DB direction); DB-introspection
   * providers leave it unset, so generators fall back to verbatim emission.
   */
  defaultIsExpression?: boolean;
  isPrimaryKey?: boolean;
  // Generated/computed column expression text (engine syntax)
  computedExpression?: string;
  /**
   * True when the column is a computed/generated column (MSSQL
   * `sys.columns.is_computed`). EMISSION-ONLY (NOT part of the compared-field
   * set in columnChangesDetector). It lets the script generator detect a
   * computed column whose expression could NOT be read (NULL definition /
   * missing VIEW DEFINITION permission) and warn loudly instead of silently
   * emitting a plain column. Populated only by the MSSQL fetcher; unset for
   * other engines/columns.
   */
  isComputed?: boolean;
  /**
   * True when a generated/computed column is materialised on disk rather than
   * evaluated on read. EMISSION-ONLY for the shared comparer (NOT part of the
   * compared-field set in columnChangesDetector — the Table Designer compares
   * it itself). Governs the persistence keyword the emitted computed-column DDL
   * must restate: MSSQL ` PERSISTED`, MySQL ` STORED` vs ` VIRTUAL`,
   * PostgreSQL ` STORED`.
   *
   * Introspection populates it for SQL Server
   * (`sys.computed_columns.is_persisted`), MySQL (EXTRA `STORED GENERATED` /
   * `VIRTUAL GENERATED`) and PostgreSQL (`attgenerated` `s`/`v`); the Database
   * Blueprint DDL parse layer sets it from the declared STORED / VIRTUAL /
   * PERSISTED keyword (MySQL's keyword-less generated column is VIRTUAL, so
   * `false`). Unset = a non-generated column, or an engine/source that does not
   * report persistence — the generator then emits no persistence keyword.
   */
  isPersisted?: boolean;
  // Identity seed/increment (string for Oracle precision; covers MSSQL/Oracle)
  identitySeed?: string;
  identityIncrement?: string;
  collation?: string;
  /**
   * The SCHEMA a PostgreSQL collation was spelled with — `COLLATE s1."c"` gives
   * `collationSchema: 's1'` next to `collation: 'c'`. EMISSION-ONLY (NOT part of
   * the compared-field set in columnChangesDetector — the diff stays keyed on
   * `collation`): introspection reports only `pg_collation.collname`, never its
   * namespace, so comparing the qualifier would flag every hand-qualified parsed
   * column as modified forever. Populated by the PostgreSQL DDL parse layer
   * (Database Blueprint folders, Table Designer definitions) so generators emit
   * `COLLATE "s1"."c"` instead of resolving a bare name through `search_path`;
   * the Table Designer compares it itself and scripts a qualifier-only change
   * (`electron/table-designer/designerCollationSchema.ts`).
   *
   * `collation` stays the collation's OWN name on both sides and is NEVER split
   * on its dots — `en_US.utf8` and `C.UTF-8` are single names in `pg_collation`.
   * Unset everywhere else.
   */
  collationSchema?: string;
  comment?: string;
  /**
   * True when the column is a synthesised view of an engine-internal
   * pseudo-column rather than a user-declared column (mirrors
   * `ColumnMetadata.isSynthetic` in `electron/metadataService.ts`, populated
   * only by the SQLite metadata service for the `rowid` alias it injects when
   * a table has no user-declared PK). EMISSION-ONLY. Data Import's existing-table
   * append mode (`electron/data-import/targetTableColumns.ts`) excludes synthetic
   * columns from insertable mapping targets — the DB fills them itself.
   */
  isSynthetic?: boolean;
};

/**
 * Constraint metadata within a table snapshot.
 */
export type SchemaConstraintSnapshot = {
  name: string;
  type: 'PRIMARY KEY' | 'FOREIGN KEY' | 'UNIQUE' | 'CHECK' | 'DEFAULT';
  columns: string[];
  definition?: string;
  referencedSchema?: string;
  referencedTable?: string;
  referencedColumns?: string[];
  // FK referential actions (e.g. 'CASCADE', 'SET NULL'); absent = engine default.
  onDelete?: string;
  onUpdate?: string;
};

/**
 * Index metadata within a table snapshot.
 */
export type SchemaIndexSnapshot = {
  name: string;
  columns: string[];
  isUnique: boolean;
  isClustered?: boolean;
  // Non-key covering columns (MSSQL INCLUDE / PG INCLUDE), separate from key columns.
  includedColumns?: string[];
  // Partial-index predicate (MSSQL filter_definition / PG WHERE), engine-native text.
  filterDefinition?: string;
};

/**
 * Trigger metadata within a table snapshot.
 *
 * `definition` is the engine's full `CREATE TRIGGER ...` text (or, for engines
 * that don't expose original text such as MySQL, a reassembled equivalent).
 * For v1 we only compare `name` for matching and `definition` (whitespace-
 * normalized) for equality.
 */
export type SchemaTriggerSnapshot = {
  name: string;
  definition: string;
  /**
   * See `SchemaViewSnapshot.definitionUnreadable`. Triggers hit exactly the
   * same seam: MSSQL's `sys.triggers` row is visible to anyone who can see the
   * PARENT TABLE, but the `LEFT JOIN sys.sql_modules` yields a NULL definition
   * without `VIEW DEFINITION`. Coercing that to `''` made a table whose only
   * difference is a trigger body compare as identical — and, because trigger
   * diffs are the only trigger contribution to the parent table's verdict, the
   * whole table then dropped out of the results (F01).
   */
  definitionUnreadable?: boolean;
};

/**
 * Table snapshot with all metadata.
 */
export type SchemaTableSnapshot = {
  schema: string;
  name: string;
  columns: SchemaColumnSnapshot[];
  constraints: SchemaConstraintSnapshot[];
  indexes: SchemaIndexSnapshot[];
  triggers: SchemaTriggerSnapshot[];
  // Cross-engine table-level comment.
  comment?: string;
  // Cross-engine partition definition (definition-level only for v1).
  partition?: SchemaPartitionSnapshot;
  // Engine-specific options, discriminated by `engine` field.
  engineOptions?: EngineSpecificTableOptions;
};

/**
 * View snapshot with definition.
 */
export type SchemaViewSnapshot = {
  schema: string;
  name: string;
  definition: string;
  /**
   * True when the server did NOT hand back a definition for this object — the
   * fetch produced `null`, not `''`. `definition` is then a placeholder and
   * carries no information. See `UnreadableSchemaObject` for why the two
   * states must never be collapsed (F01).
   */
  definitionUnreadable?: boolean;
};

/**
 * Routine (stored procedure or function) snapshot.
 */
export type SchemaRoutineSnapshot = {
  schema: string;
  name: string;
  routineType: 'procedure' | 'function';
  definition: string;
  /** See `SchemaViewSnapshot.definitionUnreadable`. */
  definitionUnreadable?: boolean;
};

/**
 * Sequence snapshot with properties.
 */
export type SchemaSequenceSnapshot = {
  schema: string;
  name: string;
  incrementBy: number;
  minValue: string;
  maxValue: string;
  /**
   * The sequence's declared numeric type, lower-cased and space-free
   * (`int`, `bigint`, `decimal(38,0)`, `integer`, …).
   *
   * SQL Server and PostgreSQL both declare one (`CREATE SEQUENCE x AS int`);
   * Oracle has no such concept and leaves this undefined. It is NOT cosmetic:
   * a `decimal(38,0)` sequence's bounds do not fit in the `bigint` a missing
   * `AS` clause defaults to, and SQL Server rejects the resulting DDL outright
   * with error 11708 — measured, by executing our own generated DDL against
   * Azure SQL Edge. Two sequences that differ only in declared type are also
   * genuinely different objects, so the comparer diffs this too.
   */
  dataType?: string;
  /** Cached values per allocation. `0` means the sequence does not cache. */
  cacheSize: number;
  /**
   * True when the sequence caches with a size the ENGINE chooses rather than
   * one the DDL pins, in which case `cacheSize` carries no information.
   *
   * Only SQL Server has this state, and it is its DEFAULT: a plain
   * `CREATE SEQUENCE x AS int START WITH 1` stores `is_cached = 1` with
   * `cache_size = NULL` (measured on Azure SQL Edge 15.0.2000). Neither 0 nor
   * an invented number describes that honestly — 0 would emit `NO CACHE`,
   * which changes the sequence's behaviour on apply, and a hard-coded 50 would
   * pin a size the source never pinned, hiding a real difference against a
   * sequence explicitly declared `CACHE 50`. The emitters write a bare `CACHE`
   * instead, which is exactly what the source DDL said.
   *
   * Oracle and PostgreSQL always report a concrete size, so this is never set
   * for them.
   */
  cacheDefault?: boolean;
  cycle: boolean;
  /** Oracle-only: guaranteed ordering across RAC instances. */
  ordered: boolean;
};

/**
 * The three kinds of SQL Server user-defined type (S7). Same literals as the
 * Object Explorer's `MssqlUserTypeKind` (`mssqlUserTypeScript.ts`), which is
 * what every `definition` below is rendered by.
 */
export type SchemaUserTypeKind = 'table-type' | 'alias-type' | 'clr-type';

/** Every kind, in listing order — the fetcher's "all listings failed" test (S15/H1). */
export const SCHEMA_USER_TYPE_KINDS: readonly SchemaUserTypeKind[] = ['table-type', 'alias-type', 'clr-type'];

/**
 * Something on the SAME endpoint that references a user-defined type and
 * therefore blocks `DROP TYPE` (Msg 3732). Read from
 * `sys.sql_expression_dependencies WHERE referenced_class = 6` for modules, and
 * from `sys.columns` for tables whose column is typed with an alias type.
 */
export type SchemaUserTypeReference = {
  schema: string;
  name: string;
  /**
   * `table-type` — a column of ANOTHER table type is declared with this
   * (alias) type. Reported as the OWNING type (1.4.21 S15/H5), not as the
   * `sys.TT_<Name>_<hex>` backing object SQL Server files it under, so the
   * blocker text names something the user can find and the removed-type
   * guard can match it against the `userTypes.<s>.<n>` key this script drops.
   */
  kind: 'procedure' | 'function' | 'view' | 'trigger' | 'table-column' | 'table-type';
};

/**
 * User-defined type snapshot (SQL Server only — S7).
 *
 * `definition` is the canonical `CREATE TYPE …` text produced by the Object
 * Explorer's own builders (`buildCreateTableTypeScript` & co.), so the DDL tab,
 * Script as CREATE and the sync script are byte-identical. The comparer diffs
 * this text: the builders are deterministic, so two types that render the same
 * ARE the same. SQL Server has no `ALTER TYPE`, which is why `referencedBy` is
 * carried — a changed type can only be dropped and re-created, and the script
 * generator has to know what stands in the way.
 */
export type SchemaUserTypeSnapshot = {
  schema: string;
  name: string;
  kind: SchemaUserTypeKind;
  definition: string;
  /**
   * True when the catalog listed the type but the rows its definition is
   * rebuilt from were missing (a table type with no `sys.columns` rows, an
   * alias whose base type could not be resolved, a CLR type with no assembly
   * binding). `definition` is then a placeholder comment and the type gets no
   * verdict — see `UnreadableSchemaObject` for why that must never collapse
   * into "identical" (F01).
   */
  definitionUnreadable?: boolean;
  /** Objects on this endpoint that reference the type. Absent when none do. */
  referencedBy?: SchemaUserTypeReference[];
  /**
   * True when the dependency read (`sys.sql_expression_dependencies` +
   * `sys.columns`) FAILED on this endpoint, so `referencedBy` is unknown
   * rather than empty (1.4.21 S15/H1). The script generator must then
   * withhold a modified / removed type's DROP TYPE — Msg 3732 would refuse it
   * for a reference nobody could see — instead of reading "no references".
   */
  referencesUnreadable?: boolean;
};

/**
 * One catalog read the user-type fetch could not complete (1.4.21 S15/H1).
 * `read` names the read in the provider's own vocabulary (`table-type
 * listing`, `references`, …) and `catalogView` the system view it is over —
 * the only name that ever reaches a warning, so a restricted login's failure
 * can be disclosed without quoting the server's error text.
 */
export type SchemaUserTypeReadFailure = {
  read: string;
  catalogView: string;
};

/**
 * What `SchemaFetchProvider.fetchUserTypes` returns (1.4.21 S15/H1). The
 * catalog settles its reads one by one instead of all-or-nothing, so a login
 * that can list the types but not read one detail view still gets a result:
 * the types it could read, the reads it could not, and the KINDS whose
 * listing failed outright (those types are simply not here, and the comparer
 * must not read their absence as "this side has none").
 */
export type SchemaUserTypeFetchResult = {
  userTypes: SchemaUserTypeSnapshot[];
  /** Absent or empty when every read succeeded. */
  readFailures?: SchemaUserTypeReadFailure[];
  /** Kinds whose listing read failed — nothing of that kind is in `userTypes`. */
  kindsNotListed?: SchemaUserTypeKind[];
};

/**
 * An object whose DEFINITION could not be read from the server.
 *
 * `null` (could not read — permissions, encryption, or an unavailable source)
 * and `''` (read successfully and genuinely empty) are DIFFERENT states and
 * must never be collapsed into one. On MSSQL a login without `VIEW DEFINITION`
 * still sees the object in `sys.views` / `sys.procedures`, but the matching
 * `sys.sql_modules` row comes back with `definition = NULL`. Coercing that to
 * `''` is what made Schema Compare report two unreadable objects as
 * "identical" — a confidently wrong verdict about something it never read
 * (F01). Every definition provider therefore returns `string | null`, and the
 * fetcher records the nulls here instead of coercing them away.
 */
export type UnreadableSchemaObject = {
  objectType: 'view' | 'procedure' | 'function' | 'trigger' | 'userType';
  schema: string;
  name: string;
  /** Set for `trigger` only: the table the trigger is attached to. */
  parentTable?: string;
};

/** An `UnreadableSchemaObject` tagged with the endpoint it was fetched from. */
export type SchemaCompareUnreadableObject = UnreadableSchemaObject & {
  side: 'source' | 'target';
};

/**
 * Which optional per-table enrichment query failed. The base columns/
 * constraints/indexes read for the table still succeeded — only this facet
 * is missing, not "this table has none" (F01). Shared between
 * `electron/metadataService.ts` (where the enrichment queries actually run
 * and best-effort `catch` them) and the schema-compare fetch path, which
 * threads per-table failures through the same two-level split
 * `UnreadableSchemaObject` uses for withheld object definitions.
 */
export type MetadataEnrichmentKind = 'tableOptions' | 'partition' | 'columnEnrichments';

/**
 * A per-table metadata enrichment that failed during a fetch. The table
 * itself still fetched successfully — `which` facet came back missing.
 */
export type TableMetadataEnrichmentFailure = {
  which: MetadataEnrichmentKind;
  schema: string;
  table: string;
};

/** A `TableMetadataEnrichmentFailure` tagged with the endpoint it was fetched from. */
export type SchemaCompareEnrichmentFailure = TableMetadataEnrichmentFailure & {
  side: 'source' | 'target';
};

/**
 * The per-table PROPERTY classes a Quick-depth fetch can leave unread. Which
 * ones it ACTUALLY leaves unread is engine-dependent, which is the whole reason
 * this type exists rather than one boolean — see `TableDetailClass` usage in
 * `schemaFetcher.ts` and §2.3.
 *
 * Not the same axis as the OBJECT types in `object-types-not-compared`: those
 * are tables/views/routines/sequences, one level up.
 */
export type TableDetailClass =
  | 'constraints'
  | 'indexes'
  | 'triggers'
  | 'tableOptions'
  | 'partition';

/**
 * The order every surface lists these in. Declared once so the same pair of
 * endpoints always produces the same sentence, whichever side was fetched
 * first and whichever order a producer happened to build its list in.
 */
export const TABLE_DETAIL_CLASS_ORDER: readonly TableDetailClass[] = [
  'constraints',
  'indexes',
  'triggers',
  'tableOptions',
  'partition',
];

/**
 * Renderer-facing label for each class. Kept beside the type so a class added
 * to the union cannot reach a surface unlabelled — every consumer reads its
 * wording from here rather than spelling its own.
 */
export const TABLE_DETAIL_CLASS_LABELS: Readonly<Record<TableDetailClass, string>> = {
  constraints: 'constraints',
  indexes: 'indexes',
  triggers: 'triggers',
  tableOptions: 'table options',
  partition: 'partition definitions',
};

/**
 * The same labels in the singular, for the one surface that needs them: the AI
 * summary's instruction clause ("do not add, drop or alter any constraint,
 * index or trigger from this summary"). Spelled out rather than derived by
 * trimming a trailing `s` — that rule turns "indexes" into "indexe", and the
 * clause it lands in is an instruction to an agent, where a garbled noun is the
 * kind of thing that gets the whole sentence discounted.
 */
export const TABLE_DETAIL_CLASS_SINGULAR_LABELS: Readonly<Record<TableDetailClass, string>> = {
  constraints: 'constraint',
  indexes: 'index',
  triggers: 'trigger',
  tableOptions: 'table option',
  partition: 'partition definition',
};

/**
 * Non-fatal warning attached to a COMPLETED comparison. Unlike
 * `errorSide`/`errorPhase` on `RunSchemaCompareResult` (F12), which describe a
 * run that failed outright, these describe a run that produced results the
 * user must not read at face value. Wording lives in the renderer — this
 * carries only low-cardinality facts.
 */
export type SchemaCompareWarning =
  | {
      kind: 'unreadable-definitions';
      side: 'source' | 'target' | 'both';
      /**
       * DISTINCT definitions that could not be read, de-duplicated across sides —
       * an object withheld on both endpoints is one unreadable definition, not two.
       * (`SchemaDiffResult.unreadable` keeps the per-side entries.)
       */
      count: number;
    }
  | {
      /**
       * The endpoint could not ENUMERATE triggers at all, so their absence
       * proves nothing. Distinct from `unreadable-definitions`, which is
       * "we saw the object and could not read its body".
       *
       * MySQL is the engine this exists for: a connection without the `TRIGGER`
       * privilege gets ZERO ROWS from `information_schema.TRIGGERS` rather than
       * rows with blank bodies, so a body-level guard can never fire. Without
       * this flag, comparing a privileged endpoint against an unprivileged one
       * reports every trigger as added/removed and every triggered table as
       * modified — phantom differences, no warning.
       */
      kind: 'triggers-not-enumerable';
      side: 'source' | 'target' | 'both';
      /**
       * D6 — how many trigger verdicts this blindness produced, i.e. the
       * `unreadable` trigger diffs that exist BECAUSE a side could not list
       * them, excluding the ones whose bodies were withheld (those are already
       * in `unreadable-definitions.count`).
       *
       * The two counts partition the results tree's Unreadable badge, so the
       * banners and the badge add up instead of quietly disagreeing. Zero in
       * the symmetric case: when neither side can list a trigger there is
       * nothing to disagree about and therefore no verdict to show — which is
       * exactly why the warning itself has to carry the message.
       */
      count: number;
    }
  | {
      /**
       * F01/W2.2 — one or more per-table enrichment queries (table options,
       * partition definition, per-column enrichment: computed expression,
       * identity, collation, comment) threw, and `getTableMetadata` still
       * returned a SUCCESSFUL result with that facet missing. Distinct from
       * `unreadable-definitions`, which is a whole object's withheld body;
       * this is one facet of a table that otherwise fetched fine.
       *
       * The missing facet then either differs from the other side's present
       * value (phantom `modified`) or matches the other side's SAME failure —
       * both blank, phantom `identical`, the worse case and the exact shape
       * of F01. Neither is corrected here; this warning only makes the run
       * visible as untrustworthy instead of silently confident. Per-table
       * detail lives on `SchemaDiffResult.enrichmentFailures`.
       */
      kind: 'metadata-enrichment-failed';
      side: 'source' | 'target' | 'both';
      /**
       * DISTINCT (table, enrichment-kind) pairs that failed, de-duplicated
       * across sides — the same table's same facet failing on both endpoints
       * is one unusable fact, not two (mirrors `unreadable-definitions.count`).
       */
      count: number;
    }
  | {
      /**
       * W2.3 — a PRE-SCAN PREDICTION, not a post-hoc observation like the
       * other three members: `HAS_PERMS_BY_NAME(DB_NAME(), 'DATABASE', 'VIEW
       * DEFINITION')` returned 0 for this side before the scan began (MSSQL
       * only — see `SchemaFetchProvider.checkDefinitionVisibility`).
       *
       * SQL Server also grants VIEW DEFINITION per OBJECT, which this single
       * database-scope query cannot see. Measured against a live container
       * (W2.3 task report): a login with ONLY a per-object grant still reads
       * `0` here even though the object's definition is fully readable. So a
       * `0` means "this side's login MAY have trouble reading definitions",
       * not "it will" — keep the renderer's copy hedged ("may be unreadable")
       * for exactly that reason; do not sharpen it into a certainty.
       *
       * Recorded here — not just shown as a one-time pre-scan dialog — so the
       * post-scan results banner corroborates the pre-scan warning instead of
       * contradicting it when the scan itself finds nothing unreadable
       * (the case-5 shape above is precisely when that would otherwise
       * happen: everything readable via per-object grants, zero
       * `unreadable-definitions`, but the login truly did lack the
       * database-scope right).
       */
      kind: 'view-definition-permission-missing';
      side: 'source' | 'target' | 'both';
    }
  | {
      /**
       * This side's login could not see part of the catalog, so some objects
       * are **absent from the listing** this comparison read — with `count`
       * of them when the engine could say how many. Produced by
       * `SchemaFetchProvider.assessCatalogVisibility`, which every engine now
       * answers; see the U7 section below for the per-engine measurements and
       * for why the member is no longer named after SQL Server's `DENY`.
       *
       * The rest of this comment describes the SQL Server shape (#93) that the
       * detector was first built for; Oracle and MySQL reach the same member
       * through their own catalogs.
       *
       * A DIFFERENT failure class from every member above, and from
       * `view-definition-permission-missing` in particular. That one predicts
       * "bodies may be unreadable" — the object still comes back, with a
       * withheld definition, and F01/W2.1 flag it per object. Here the object
       * does not come back AT ALL: `sys.tables`/`sys.views`/`sys.procedures`
       * are metadata-visibility filtered, so a denied object is indistinct
       * from one that does not exist.
       *
       * Why it needs its own detector rather than riding the W2.3 probe —
       * MEASURED on a live server (#93), running the provider's own listing
       * SQL as two logins against the same 5-object database:
       *
       * | login shape                       | W2.3 probe | DENY rows | catalog |
       * |---|---|---|---|
       * | blanket GRANT + per-object DENY   | 1 (silent) | 3 (fires) | 2 of 5  |
       * | per-object GRANT only, no blanket | 0 (fires)  | 0 (silent)| 1 of 5  |
       *
       * The two are complementary: neither shape is caught by the other's
       * detector. The first row is also invisible to S4's wholesale-drop gate
       * (`wholeTargetDrop.ts`), which fires only when a script would drop
       * EVERY object examined — here most objects are still visible.
       *
       * `count` is DISTINCT denied objects, and it is all we can honestly
       * report: `OBJECT_NAME(major_id)` returns NULL for exactly these
       * objects (measured), so the names cannot be resolved by the very login
       * that is blind to them. Copy must not promise to name them.
       *
       * ## U7 — why this member is no longer named after SQL Server's DENY
       *
       * #93 shipped this as `objects-hidden-by-deny`, MSSQL-only, and the
       * cross-engine hole that left was never written down: the fetcher's
       * probe returned `0` for every other engine, so a login that genuinely
       * could not see part of a MySQL or Oracle catalog produced no gate and
       * no disclosure, and its invisible objects became DROPs.
       *
       * Two of the four remaining engines have the same failure and neither
       * of them has a `DENY` (measured 2026-08-11 against the project's own
       * containers — see `SchemaFetchProvider.assessCatalogVisibility` for
       * the per-engine measurements):
       *
       * | engine | listing read by the provider | can a permitted-but-blind login miss an object? |
       * |---|---|---|
       * | MSSQL | `sys.tables` … | YES — metadata-visibility filtered |
       * | Oracle | `ALL_TABLES` / `ALL_OBJECTS` | YES — "accessible to the current user" |
       * | MySQL | `information_schema.TABLES` | YES — rows require a privilege on the table |
       * | PostgreSQL | `pg_class` / `pg_proc` | **NO** — `pg_catalog` is world-readable |
       * | SQLite | `sqlite_master` | **NO** — no permission model at all |
       *
       * So the union member had to widen from "the server counted N DENYs" to
       * "this side's catalog may be short, and here is the count if the server
       * could give us one". That is one statement with two evidence levels, not
       * two statements: every consumer renders the same CONSEQUENCE (an object
       * absent from one side's listing is indistinguishable from one that was
       * deleted, so a DROP may be aimed at an object that exists on both
       * sides), and only the sentence that justifies it changes. Splitting it
       * into a second `kind` would have duplicated all five consumers and made
       * a cross-engine compare (MSSQL source, MySQL target) emit two warnings
       * that say the same thing.
       *
       * Keeping the old name was not an option: three of the four engines that
       * can hide an object have no `DENY` concept, so the name would have been
       * a lie in the type itself. Renaming was free — #93 is unreleased
       * (1.4.18 is untagged), and both exhaustive `Record<…['kind'], …>` maps
       * turn the rename into a compile error at every consumer rather than a
       * silent miss.
       */
      kind: 'catalog-may-be-incomplete';
      side: 'source' | 'target' | 'both';
      /**
       * DISTINCT objects the SERVER ITSELF reported it filtered out of this
       * login's catalog, summed over the sides that fired.
       *
       * Present only when EVERY side that fired could produce a number
       * (MSSQL's `DENY` row count; Oracle's `DBA_OBJECTS` − `ALL_OBJECTS`
       * difference). Absent when any firing side could only establish that its
       * visibility is granted object-by-object without being able to count
       * what it cannot see — MySQL always, Oracle when `DBA_OBJECTS` is not
       * readable. A partial sum would read as a complete one, so the number is
       * dropped rather than understated and the copy says "may be" instead.
       */
      count?: number;
    }
  | {
      /**
       * I5 fix-round-2 (item 4) — source and target databases have different
       * own DEFAULT collations (MSSQL only). Merged here from
       * `feat/soporte4-compare-options`, which grew its own single-member
       * `SchemaCompareWarning` deliberately shaped to union-merge onto this
       * one rather than opening a second warnings channel.
       *
       * Unlike the four members above this is a comparison-SEMANTICS
       * disclosure, NOT a "something was skipped" fact — see this kind's
       * entry in `SCHEMA_COMPARE_WARNING_MEANS_NOT_COMPARED` below.
       *
       * Surfaced at COMPARE level (not script-generation level): a user who
       * never opens Generate Script must still see it, since that's the
       * surface a real report showed users give up before reaching.
       */
      kind: 'differing-default-collations';
      sourceDefaultCollation: string;
      targetDefaultCollation: string;
    }
  | {
      /**
       * U6 — this side was fetched at **Quick** depth (`options.detailed ===
       * false`), so `getTableMetadata` returned `constraints: []` /
       * `indexes: []` and never queried table options or the partition
       * scheme (`electron/metadataService.ts`'s light-mode early return), and
       * `schemaFetcher.ts` skipped triggers entirely. Five whole property
       * classes were never read.
       *
       * Why this has to be a warning rather than a line in the W5.3
       * "Skipping: …" summary, which is where the wave first tried to put it:
       * that summary renders from `ignoredProperties`, and Quick's
       * `ignoredProperties` is `[]` (`compareDepth.ts`'s `applyDepthPreset`),
       * so it renders NOTHING. More importantly the summary is a toolbar
       * label — it is not consulted by `someWarningMeansNotCompared`, so the
       * Overview went on rendering **"Schemas are identical"** over a
       * comparison that never looked at a constraint, an index or a trigger.
       * That verdict, not the missing label, is the defect.
       *
       * Recorded per SIDE and derived from the SNAPSHOT
       * (`SchemaSnapshot.tableDetailSkipped`), never from `options.detailed`
       * directly, because a folder endpoint (Database Blueprint) reads its
       * full table shape off disk regardless of depth — claiming its
       * constraints were skipped would be a fresh lie in the opposite
       * direction. Only the endpoint that actually took the light path says
       * so.
       *
       * Nothing here is a permission problem: the user chose this, and Deep /
       * Standard re-run the compare in full. The copy must therefore point at
       * the depth control, not at a grant.
       */
      kind: 'table-detail-not-compared';
      side: 'source' | 'target' | 'both';
      /**
       * §2.3 — WHICH property classes actually went uncompared, rather than the
       * blanket "all five" this used to imply.
       *
       * The blanket wording was false on SQLite, and false in the direction
       * that costs the user the most: the SQLite fetch never consults
       * `detailed` at all (`schemaFetcher.ts` forks to
       * `getSqliteTableMetadata`, which has no such parameter), so its
       * constraints and table options are fetched and compared at EVERY depth
       * — while the banner told the reader they had never been read, and the
       * MCP disclosure instructed an agent not to act on them. A correct,
       * actionable constraint difference sat underneath a notice saying to
       * ignore it.
       *
       * Always non-empty when this warning is present: a warning that named no
       * class would be the same empty claim in a new shape.
       */
      classes: TableDetailClass[];
    }
  | {
      /**
       * #6 — one or more whole OBJECT TYPES were switched off for this run
       * (`includeViews: false` and friends), so nothing of that type was
       * fetched, compared or verdicted on either endpoint.
       *
       * The same undisclosed-scope class as U6 above, one level up: U6 hides
       * five per-table PROPERTY classes, this hides whole object types. The
       * results surface was equally silent about both. With Views off there
       * are zero view diffs, so `buildOverviewSummary` omits the Views
       * category entirely — indistinguishable from "this database has no
       * views" — and the Overview asserted **"Schemas are identical"** with
       * the emerald tick underneath. The only hedge was the sentence "Source
       * and target schemas match across all compared object types", which
       * never named which types those were, and which is the exact failure
       * mode this warning's copy must not reproduce: it carries the list.
       *
       * Derived from `SchemaDiffResult.declaredScope` (via
       * `objectTypesNotCompared`), never from `options.includeX` at the
       * consuming surface, for the reason the scope contract exists at all —
       * the results UI, the script generator and an MCP caller all outlive
       * the request that produced them, and `includeViews` reached NONE of
       * them before this. Not per-`side`: the include flags are a single
       * request-level set that `compareSchemas` records identically on both
       * endpoints, and the composition is a union of the two endpoints'
       * exclusions precisely because scope membership is their intersection.
       *
       * Types the endpoints' engines cannot have are filtered out at the
       * producer, so a SQLite folder compare (no procedures, no functions, no
       * sequences to begin with) does not fire. A banner on every comparison
       * would be worse than the bug it discloses.
       */
      kind: 'object-types-not-compared';
      /**
       * The EXCLUDED types, in `SCHEMA_COMPARE_OBJECT_TYPES` order. Never
       * empty — the producer omits the warning entirely rather than emitting
       * an empty list, so no consumer has to defend against "nothing was
       * excluded" phrased as a warning.
       */
      objectTypes: SchemaCompareObjectType[];
    }
  | {
      /**
       * 1.4.21 S15/H1 — one or more of the SQL Server user-type catalog reads
       * failed on this side (a login without SELECT on, say,
       * `sys.sql_expression_dependencies` or `sys.assembly_types`), and the
       * fetch DEGRADED instead of failing the compare: a failed detail read
       * leaves the affected types `unreadable`, a failed dependency read
       * withholds every rebuild / DROP TYPE for them, a failed listing leaves
       * that kind uncompared, and every listing failing leaves user-defined
       * types out of this side's scope (`object-types-not-compared` fires
       * alongside). The comparison completed; the part that rests on these
       * reads did not.
       */
      kind: 'user-type-catalog-unreadable';
      side: 'source' | 'target' | 'both';
      /**
       * The catalog views the failed reads are over, de-duplicated across
       * sides — the ONLY names carried (no error text, no object names).
       * Never empty.
       */
      catalogViews: string[];
    };

/**
 * Fix-round-1/W2.3 — a TOTAL mapping from warning `kind` to "does this mean
 * something in the comparison was never actually read/verdicted" (as opposed
 * to a warning that discloses a fact about a comparison that ran to
 * completion — e.g. a differing-default-collations-shaped kind would be a
 * comparison-SEMANTICS disclosure: the comparison ran fully and found a real
 * difference, so nothing was skipped).
 *
 * This exists because two consumers each grew their OWN answer to that
 * question and disagreed by construction: `SchemaDiffOverview.tsx` derived it
 * generically from `warnings.length > 0` (right for every kind that existed
 * when it was written, but only by accident — a future disclosure-only kind
 * would wrongly flip it true), while `SchemaCompareTab.tsx`'s Draft-Migration
 * tooltip derived it from an explicit three-member allowlist that a fourth
 * warning kind (`view-definition-permission-missing`) was added without
 * updating — so the tooltip kept claiming "No differences found — nothing to
 * draft a migration for." (an unhedged claim of a clean, complete comparison)
 * on exactly the config-5 false-positive shape this warning exists to hedge.
 *
 * Declared as an exhaustive `Record<SchemaCompareWarning['kind'], boolean>`
 * so that adding a new `SchemaCompareWarning` member WITHOUT deciding its
 * entry here is a COMPILE ERROR (TS2741/2739, missing property), not a
 * silent miss repeating the defect above. Both call sites now consume
 * `someWarningMeansNotCompared` below instead of re-deriving their own
 * allowlist.
 *
 * RESOLVED AT MERGE: the sibling branch's `differing-default-collations` kind
 * has landed and is mapped to `false` below, for the reason this note
 * predicted — it is a comparison-SEMANTICS disclosure, not a "something was
 * skipped" fact.
 */
export const SCHEMA_COMPARE_WARNING_MEANS_NOT_COMPARED: Record<
  SchemaCompareWarning['kind'],
  boolean
> = {
  'unreadable-definitions': true,
  'triggers-not-enumerable': true,
  'metadata-enrichment-failed': true,
  'view-definition-permission-missing': true,
  // #93/U7 — `true`, and this is the least arguable `true` in the map: the
  // hidden objects were not merely unverdicted, they were never enumerated.
  // Anything the other side has that they correspond to is currently
  // classified purely from the other side's evidence. `true` regardless of
  // whether the server could COUNT them — "we know three are missing" and "we
  // cannot rule out that some are missing" license exactly the same caution
  // about the verdict.
  'catalog-may-be-incomplete': true,
  // MERGE DECISION (integration of `feat/soporte4-compare-options`, per the
  // "IMPORTANT FOR THE MERGE OWNER" note above): `false`. The comparison ran
  // to completion and found a real, fully-verdicted difference — differing
  // database default collations. Nothing was skipped or left unread, so this
  // kind must not make the Overview claim "something was not compared".
  'differing-default-collations': false,
  // U6 — `true`, on exactly the same terms as `triggers-not-enumerable`
  // above: nobody looked. The only difference is WHY nobody looked (a depth
  // preset rather than a missing privilege), and the reason does not change
  // what the absence of a constraint/index/trigger diff is worth. A Quick
  // compare that finds no differences must not be presentable as a clean
  // one.
  'table-detail-not-compared': true,
  // #6 — `true`, on the same terms as U6 directly above, and the decision this
  // kind exists to force. The user unticked the type, so it is tempting to
  // read this as a preference rather than a gap; U6 already settled that
  // argument for the depth dial, and nothing about an object type makes the
  // answer different. Zero view diffs because nobody fetched a view is not
  // evidence that the views agree, and the surface that says "Schemas are
  // identical" is the one a user screenshots and sends to a colleague.
  'object-types-not-compared': true,
  // S15/H1 — `true`: whatever rests on the failed read (a type's definition,
  // its references, a whole kind's listing) was never compared, on the same
  // terms as `unreadable-definitions` and `triggers-not-enumerable`.
  'user-type-catalog-unreadable': true,
};

/**
 * True when ANY warning in the list means part of this comparison was never
 * actually read — i.e. the caller must not present the run as clean/complete
 * on the strength of this list being non-empty alone. See
 * `SCHEMA_COMPARE_WARNING_MEANS_NOT_COMPARED` above for why this is a single
 * shared function rather than two independently-derived booleans.
 */
export const someWarningMeansNotCompared = (
  warnings: readonly SchemaCompareWarning[] | undefined
): boolean =>
  (warnings ?? []).some((w) => SCHEMA_COMPARE_WARNING_MEANS_NOT_COMPARED[w.kind]);

/**
 * #6 — the object-type exclusion warning, narrowed, for the surfaces that need
 * its `objectTypes` list rather than just the boolean above.
 *
 * A shared selector rather than a `.find()` per consumer: three surfaces read
 * it (the results banner, the Overview canvas, the AI summary) and the defect
 * this closes is a signal that was reachable from one place and not another.
 * Deliberately NOT routed through `useSchemaCompare`'s per-kind fields the way
 * the first five warnings are — those predate the shared registry, and a
 * renderer hook cannot serve `buildOverviewSummary` (pure) or the main-process
 * consumers at all.
 */
export const objectTypesNotComparedWarning = (
  warnings: readonly SchemaCompareWarning[] | undefined
): Extract<SchemaCompareWarning, { kind: 'object-types-not-compared' }> | undefined =>
  (warnings ?? []).find(
    (w): w is Extract<SchemaCompareWarning, { kind: 'object-types-not-compared' }> =>
      w.kind === 'object-types-not-compared'
  );

/**
 * U7 — one engine's answer to "did the object listing this comparison was
 * built from contain everything that is actually in the database?"
 *
 * Every engine must answer (`SchemaFetchProvider.assessCatalogVisibility` is
 * REQUIRED, not optional — that is the whole point of the type). #93 asked the
 * question only of MSSQL through an optional method, and the four engines that
 * did not implement it were silently treated as "nothing hidden" by a single
 * early return in `schemaFetcher.ts`. Two of them could in fact hide objects,
 * which turned an invisible object into a `DROP` with no gate and no
 * disclosure anywhere. A required method with a three-state answer makes
 * "this engine has nothing to disclose" a decision somebody wrote down and a
 * reviewer can see, instead of the default that falls out of an absent method.
 *
 * The three states are deliberately not a boolean plus a number: "the server
 * counted 3" and "the server cannot tell me, but its catalog is filtered by
 * privilege" license different copy and are reached by different engines.
 */
export type CatalogVisibility =
  /**
   * This login sees the whole catalog. Either the engine cannot hide an object
   * from a connected session at all (PostgreSQL's `pg_catalog` is
   * world-readable; SQLite has no permission model), or it can and the probe
   * checked and found nothing hidden.
   */
  | { kind: 'complete' }
  /**
   * The server itself reported how many objects it filtered out of this
   * login's catalog. A LOWER BOUND, never a total — see each provider's
   * implementation for what its query can and cannot see.
   */
  | { kind: 'objects-hidden'; count: number }
  /**
   * This engine's catalog listing IS filtered by privilege AND this login's
   * visibility is granted object-by-object, so objects may be missing and the
   * server offers this login no way to count them. The honest disclosure is
   * "may be incomplete" with no number.
   */
  | { kind: 'unverifiable' };

/**
 * Complete schema snapshot containing all database objects.
 *
 * `engine` is optional for back-compat with older snapshots that didn't
 * persist it. Database Blueprint's sidecar (`.jamsql/snapshot.json`) sets it so
 * the folder-source endpoint can read the blueprint back with the correct
 * dialect (otherwise comparisons silently treat PG / MySQL / Oracle / SQLite
 * blueprints as MSSQL).
 */
export type SchemaSnapshot = {
  connectionId: string;
  database: string;
  fetchedAt: string; // ISO timestamp
  tables: SchemaTableSnapshot[];
  views: SchemaViewSnapshot[];
  routines: SchemaRoutineSnapshot[];
  sequences?: SchemaSequenceSnapshot[];
  /**
   * S7 — user-defined types. PRESENT (possibly empty) only when this endpoint
   * was actually read for them: a live SQL Server fetch with
   * `includeUserTypes` on. ABSENT for every other engine, for the mock engine,
   * for a fetch that switched them off, and for a Database Blueprint folder
   * (the emitter writes no user-type files yet). The comparer treats an absent
   * collection as "not compared" — never as "this side has none" — so a
   * folder→DB compare cannot read every DB type as `removed` and script
   * `DROP TYPE` for it.
   */
  userTypes?: SchemaUserTypeSnapshot[];
  /**
   * S7 — object types this endpoint's SOURCE cannot carry at all, recorded by
   * the fetcher. A Database Blueprint folder has no user-defined-type files
   * (the emitter does not write them yet — S8), so the folder branch lists
   * `userType` here and `compareSchemas` declares it out of this endpoint's
   * scope. That is what makes the results surface say "user-defined types
   * were not compared" on a folder→DB compare instead of staying silent —
   * and it holds even for a producer that forgot to cap `includeUserTypes`.
   * Absent for every live endpoint whose user-type catalog read in full; a
   * live SQL Server endpoint whose every user-type LISTING failed (a
   * restricted login — 1.4.21 S15/H1) lists `userType` here too, with the
   * failing reads in `userTypeReadFailures`, instead of failing the compare.
   */
  objectTypesUnavailable?: SchemaCompareObjectType[];
  /**
   * 1.4.21 S15/H1 — user-type catalog reads this endpoint could not complete.
   * Present only when at least one failed; the comparer turns it into the
   * `user-type-catalog-unreadable` warning. The types themselves carry the
   * consequence (`definitionUnreadable` / `referencesUnreadable`), and a
   * kind whose listing failed is in `userTypeKindsNotListed`.
   */
  userTypeReadFailures?: SchemaUserTypeReadFailure[];
  /**
   * 1.4.21 S15/H1 — user-type KINDS this endpoint could not list at all. A
   * type of that kind on the OTHER side must get no verdict (never `added` /
   * `removed`): its counterpart here is unknown, not absent. Present only when
   * `userTypes` is present — when every kind failed, `userTypes` stays absent
   * and `objectTypesUnavailable` carries the fact instead.
   */
  userTypeKindsNotListed?: SchemaUserTypeKind[];
  engine?: string;
  /**
   * The database's own default collation (MSSQL only — `DATABASEPROPERTYEX(
   * DB_NAME(), 'Collation')`; I5 fix-round-1). Lets the script generator tell
   * an explicitly-set column collation from one merely inherited from the
   * database, and lets the compare surface a one-time disclosure when source
   * and target defaults differ. Undefined for folder sources, other engines,
   * or if the one-time catalog lookup failed — callers treat that as "unknown",
   * not "no default".
   */
  defaultCollation?: string;
  /**
   * The server's own dotted product-version string (MSSQL only —
   * `SERVERPROPERTY('ProductVersion')`, e.g. `"15.0.2000.5"`; W1.6). Lets the
   * script generator decide whether `CREATE OR ALTER` (SQL Server 2016 SP1 /
   * `13.0.4001` or later) is safe to emit for VIEW/PROCEDURE/FUNCTION, or
   * whether an older target needs the DROP+CREATE fallback instead. Undefined
   * for folder sources, other engines, or if the one-time catalog lookup
   * failed — callers treat that as "unknown", which keeps today's `CREATE OR
   * ALTER` behaviour (see `mssqlSupportsCreateOrAlter` in
   * `schemaScriptGenerator.ts`) rather than downgrading on uncertainty.
   */
  serverVersion?: string;
  /**
   * The server's `SERVERPROPERTY('EngineEdition')` (MSSQL only; S1 1.4.22) —
   * 5 = Azure SQL Database, 8 = Azure SQL Managed Instance, 9 = Azure SQL
   * Edge. Fetched in the same one-time probe as `serverVersion`, because on
   * Azure SQL Database `serverVersion` is the frozen legacy `12.0.2000.8` —
   * numerically below the 2016-SP1 `CREATE OR ALTER` floor while the engine
   * is actually evergreen. `mssqlSupportsCreateOrAlter` short-circuits to
   * "modern" for the evergreen editions instead of trusting that number.
   * Undefined for folder sources, other engines, or a failed lookup —
   * "unknown", which leaves the plain version gate in charge.
   */
  serverEngineEdition?: number;
  /**
   * W3.5 fix-round-1 Item 2 — set by every fetcher (`schemaFetcher.ts`'s DB
   * and folder branches, `mockSchemaFetcher.ts`) when this snapshot was
   * built from a `singleObject` request: every array above except the
   * matching type is already empty, narrowed at fetch time. `compareSchemas`
   * reads this to know the narrowing already happened, instead of
   * re-deriving whole-database `includeX` flags from `options` — which can
   * carry a stale whole-database default (e.g. "Views off") unrelated to
   * this run being single-object, and would otherwise silently re-discard
   * the one object the fetch deliberately kept. Any consumer of a
   * `SchemaSnapshot` can check this field to tell it was narrowed, rather
   * than having to be told out-of-band by the caller.
   */
  narrowedToObject?: SchemaCompareObjectSelection;
  /**
   * S1 — the per-endpoint schema filter this snapshot was fetched with, kept
   * for the same reason `narrowedToObject` is: the filter decides which
   * schemas exist in the arrays above, so without it a downstream consumer
   * cannot tell "this schema has no objects" from "this schema was never
   * fetched". A `removed` verdict for an object in a schema the source filter
   * excluded is the second case, and the script generator must not turn it
   * into a `DROP`.
   *
   * Absent when the endpoint declared no filter, and deliberately absent when
   * `narrowedToObject` is set — a single-object fetch ignores the filter
   * (`schemaFetcher.ts`'s `filterContext`), so recording one here would
   * describe a narrowing that never happened.
   */
  schemaFilter?: SchemaFilter;
  /**
   * S2 (#30) — objects the endpoint was explicitly told NOT to hand over, in
   * the same spirit as `schemaFilter` above: the exclusion decides which
   * objects exist in the arrays, so without it a downstream consumer cannot
   * tell "the user excluded this" from "this doesn't exist".
   *
   * Set only by the Database Blueprint folder read (`folderSchemaFetcher`),
   * which is the one fetcher with a per-object exclusion list to honour — it
   * reads `jamsql-blueprint.json`'s `excludedObjects` itself, so the exclusion
   * never has to travel through the fetch REQUEST and no producer of a
   * folder-source compare has to know to pass it. Absent on live-DB snapshots.
   */
  excludedObjects?: SchemaCompareScopeObjectRef[];
  /**
   * Objects listed by the catalog whose definition the server refused to hand
   * over. Absent/empty means everything listed was read.
   */
  unreadableObjects?: UnreadableSchemaObject[];
  /**
   * `false` when this endpoint could not ENUMERATE triggers, so an empty
   * trigger list is "we were not allowed to look", not "there are none".
   * Absent means the question does not arise for this engine (see
   * `SchemaCompareWarning`'s `triggers-not-enumerable` member).
   */
  triggersEnumerable?: boolean;
  /**
   * Per-table metadata enrichment queries that threw during this fetch
   * (F01/W2.2). Absent/empty means every enrichment succeeded for every
   * table. See `SchemaCompareWarning`'s `metadata-enrichment-failed` member.
   */
  enrichmentFailures?: TableMetadataEnrichmentFailure[];
  /**
   * W2.3 — `false` when the pre-scan permission preflight ran successfully
   * (MSSQL only) and confirmed this login lacks database-scope VIEW
   * DEFINITION. Absent means: the probe wasn't run for this engine, it ran
   * and found the grant present, or it threw and failed open — all three
   * collapse to "no warning" on purpose. See `SchemaCompareWarning`'s
   * `view-definition-permission-missing` member.
   */
  viewDefinitionGranted?: boolean;
  /**
   * #93/U7 — whether this side's login could see EVERY object in the database,
   * as answered by the engine's own `assessCatalogVisibility` probe.
   *
   * Recorded ONLY when there is something to disclose, i.e. never `{ kind:
   * 'complete' }` — an absent field therefore keeps meaning "nothing to say",
   * whether because the probe found a full catalog, because the engine cannot
   * hide objects at all (PostgreSQL, SQLite), because this is a folder
   * endpoint with no login, or because the probe threw and failed open.
   *
   * See `SchemaCompareWarning`'s `catalog-may-be-incomplete` member for why
   * this cannot ride `viewDefinitionGranted`: that one predicts withheld
   * BODIES for objects that are present, this one reports objects that are
   * ABSENT.
   */
  catalogVisibility?: CatalogVisibility;
  /**
   * U6 — the per-table property classes this snapshot did NOT read, or absent
   * when it read all of them. Their absence from `tables` above is therefore
   * not evidence that the database has none.
   *
   * §2.3 turned this from a boolean into the LIST, because the boolean was
   * engine-blind and consequently wrong on SQLite. A Quick-depth fetch does
   * NOT skip the same five classes everywhere: the SQLite path never consults
   * `detailed` at all (`schemaFetcher.ts` forks to `getSqliteTableMetadata`,
   * which takes no such parameter), so its constraints, indexes and table
   * options are read at every depth and only triggers are genuinely skipped.
   * A single boolean could not say that, and the surfaces reading it told
   * SQLite users their constraints had never been looked at while a real
   * constraint diff sat on screen underneath.
   *
   * The producer decides the list per engine; every consumer renders from it
   * rather than assuming a fixed set.
   *
   * Set by the fetchers that actually honour `detailed` — `schemaFetcher.ts`'s
   * live-DB branch and `mockSchemaFetcher.ts` — and ONLY when at least one
   * table was fetched, so a compare with no tables in scope cannot claim a
   * skip that never happened. Deliberately absent on folder snapshots
   * (`folderSchemaFetcher`, which short-circuits before `detailed` is even
   * read and always reads the full table shape off disk).
   *
   * Follow-up #7 (2026-08-11) is what put the mock fetcher on that list: it
   * used to ignore `detailed` outright and emit every property class at every
   * depth, so `table-detail-not-compared` could not fire under the mock engine
   * at all and no mock e2e could observe a scope-of-comparison bug. This
   * sentence said so, and became false the moment that landed.
   *
   * `compareSchemas` turns this into the `table-detail-not-compared`
   * warning; see that `SchemaCompareWarning` member for why the disclosure
   * has to travel this way rather than being re-derived from
   * `options.detailed` at each surface.
   */
  tableDetailSkipped?: TableDetailClass[];
};

// =============================================================================
// Schema Compare Timing (S2 — "how long did it take, and how was it read?")
// =============================================================================

/**
 * How one side's schema was actually read.
 *
 * Not cosmetic, and not derivable from the connection alone at any surface
 * that shows a finished run: SQL Server picks its route per connection at
 * query time (`shouldUseSqlToolsForMssql` — Windows auth has no tedious
 * equivalent, so a Windows-auth connection silently routes every catalog
 * query through SQL Tools Service), and the two routes have completely
 * different per-query costs. A user timing a compare against two connections
 * cannot tell which side was which; without this tag neither can we.
 *
 * `'folder'` is a Database Blueprint endpoint — `.sql` files off disk, no
 * server involved at all.
 *
 * `'mixed'` (1.4.21 second review R3): the route is decided PER QUERY from
 * live state, so a driver pool that drops mid-fetch silently re-routes the
 * remainder of that side through SQL Tools. The fetcher samples the route at
 * the start and again when it stamps the timing; when the two disagree the
 * side is reported as `mixed` rather than as whichever it happened to start
 * on — a timing line that said `driver` for a side that spent most of its
 * time on SQL Tools would send the user (and us) after the wrong cost.
 */
export type SchemaFetchRoute = 'driver' | 'sql-tools' | 'folder' | 'mixed';

/**
 * What one side of a compare cost, split by phase.
 *
 * Every field is elapsed WALL CLOCK in whole milliseconds. Phase fields are
 * optional because a phase that was switched off (or that the engine has no
 * concept of) never ran — an absent `sequencesMs` means "not fetched", never
 * "instant".
 */
export type SchemaFetchSideTiming = {
  route: SchemaFetchRoute;
  /** Whole side: probes, catalog listings and every phase below. */
  totalMs: number;
  tablesMs?: number;
  viewsMs?: number;
  routinesMs?: number;
  sequencesMs?: number;
  /** S7 — the user-defined types phase (SQL Server only). */
  userTypesMs?: number;
  /**
   * S3 / 1.4.21 second review R4 — how many 300-table chunks fell back from
   * the set-based table-detail read to the six-queries-per-table read (a
   * hard family — columns, constraints, indexes — failed; after the first
   * such failure the run stops attempting bulk at all). Absent when zero,
   * so a normal run does not carry the field. Recorded so the "542 → 20
   * queries per side" win cannot regress silently behind a `console.warn`.
   */
  tableDetailDegradedChunks?: number;
};

/**
 * What a whole compare run cost — both reads and the comparison itself.
 *
 * Optional on the run result, and absent rather than zeroed whenever any part
 * of it is unknown (a rediff served from the cached snapshot pair never read a
 * database, so it has no read to report). A surface rendering this must omit
 * itself entirely when the field is undefined rather than print zeros, which
 * would read as "instant".
 *
 * WHY `compareMs` IS SEPARATE FROM THE TWO READS. `onProgress({phase:
 * 'complete'})` fires when the FETCH ends and `compareSchemas` then runs
 * synchronously with the progress bar already at 100% — so from the user's
 * seat the comparer's cost is indistinguishable from the app hanging. It is
 * also the one part of the run whose cost scales with routine/view BODIES
 * rather than with round trips, which makes it the number that separates a
 * slow network from a slow comparison.
 *
 * `engine` rides along because the two consumers of this type — the results
 * line and the `feature:schema_compare_completed` event — both have to name it
 * and neither can derive it: the renderer's hook sees connection ids, not
 * engines.
 */
export type SchemaCompareRunTiming = {
  /** The engine the comparison ran as (the SOURCE endpoint's). */
  engine: string;
  source: SchemaFetchSideTiming;
  target: SchemaFetchSideTiming;
  /** `compareSchemas` alone, bracketed at its call site. */
  compareMs: number;
  /** Handler entry to comparison end — includes the object-count probes. */
  totalMs: number;
  /**
   * Whether the two side reads overlapped (`schemaFetchesMayRunInParallel`).
   * Without it, `source.totalMs + target.totalMs` reads as the wall clock and
   * a parallel run looks like it lost time it never spent.
   */
  fetchesRanInParallel: boolean;
};

/**
 * Result from fetching a schema snapshot.
 */
export type FetchSchemaSnapshotResult = {
  success: boolean;
  snapshot?: SchemaSnapshot;
  error?: string;
  cancelled?: boolean;
  /**
   * S2 — what this read cost and which route served it. Present on success
   * only: a failed or cancelled fetch stopped partway, and a duration for a
   * read that did not finish would be quoted as if it had.
   */
  timing?: SchemaFetchSideTiming;
};

// =============================================================================
// Schema Compare Permission Preflight (W2.3)
// =============================================================================

/**
 * W2.3 — request to check, BEFORE a (potentially long) compare begins,
 * whether this login likely has enough rights to read object definitions.
 * One cheap query per endpoint — the renderer calls this once per side,
 * before `schemaCompare.runCompare`, so it can offer "Continue anyway /
 * Cancel" before any per-table progress starts.
 */
export type SchemaComparePermissionPreflightRequest = {
  connectionId: string;
  database: string;
};

export type SchemaComparePermissionPreflightResult = {
  /**
   * `false` ONLY when the probe ran successfully on an engine that supports
   * it (MSSQL today) and confirmed the login lacks database-scope VIEW
   * DEFINITION. Every other case — an unsupported engine, a mock connection,
   * or a probe that threw — reports `true` so the caller fails open. A
   * preflight that cannot check must never block or discourage a compare.
   */
  hasDefinitionVisibility: boolean;
};

/**
 * Preflight data for main-process license enforcement.
 * The renderer should include this when available from preflight checks.
 */
export type SchemaComparePreflightData = {
  /** Source schema total object count (from preflight) */
  sourceObjectCount?: number;
  /** Target schema total object count (from preflight) */
  targetObjectCount?: number;
};

/**
 * Object type for single-object schema comparison.
 *
 * `userType` (S7) is a SQL Server user-defined type — table type, alias type
 * or CLR type — the one kind whose name in the results tree, the selection
 * keys (`userTypes.<schema>.<name>`) and the DDL request is `userType` rather
 * than a word the engine uses, because SQL Server itself files all three
 * under "Types".
 */
export type SchemaCompareObjectType =
  | 'table'
  | 'view'
  | 'procedure'
  | 'function'
  | 'sequence'
  | 'userType';

/**
 * Single object selection for schema comparison.
 */
export type SchemaCompareObjectSelection = {
  objectType: SchemaCompareObjectType;
  objectSchema: string;
  objectName: string;
};

// =============================================================================
// Declared scope (S1) — what the user asked to compare
// =============================================================================

/** One object named by a declared scope or by a refusal. */
export type SchemaCompareScopeObjectRef = {
  objectType: SchemaCompareObjectType;
  schema: string;
  name: string;
};

/**
 * What ONE endpoint of a comparison was asked to hand over.
 *
 * Recorded by the fetcher onto its `SchemaSnapshot` (see `narrowedToObject` /
 * `schemaFilter` there) and composed into `SchemaDiffResult.declaredScope` by
 * `compareSchemas`, so a consumer that only ever sees the diff — the script
 * generator runs long after the request is gone, and the MCP path rebuilds its
 * selection from the result alone — can still tell whether an object was ever
 * in scope.
 *
 * Every field is OPTIONAL and every absent field means "unrestricted". An
 * endpoint that declared nothing therefore constrains nothing; see
 * `isObjectInDeclaredScope` for why that fails open rather than closed.
 */
export type SchemaCompareEndpointScope = {
  /** Narrowed to exactly one object at fetch time. */
  object?: SchemaCompareObjectSelection;
  /** The per-endpoint schema include/exclude filter this side was fetched with. */
  schemaFilter?: SchemaFilter;
  /**
   * The object types this side was asked for (from `SchemaCompareOptionsDto`'s
   * `includeTables`/`includeViews`/`includeProcedures`/`includeFunctions`/
   * `includeSequences`/`includeUserTypes`). Absent means all types.
   */
  objectTypes?: SchemaCompareObjectType[];
  /**
   * Objects the caller explicitly excluded — the Database Blueprint
   * `excludedObjects` case (#30).
   *
   * **Populated by S2**, read off `SchemaSnapshot.excludedObjects` (which the
   * folder fetcher records from `jamsql-blueprint.json`) exactly the way
   * `object` and `schemaFilter` are read off their snapshot fields. It is
   * deliberately NOT a field on `FetchSchemaSnapshotRequest` or
   * `McpSchemaCompareStartInput`: the exclusions live on disk beside the DDL
   * the folder read already parses, so making the REQUEST carry them would
   * oblige every producer of a folder-source compare to read the manifest and
   * pass them — and the Apply-to-linked-DBs batch reads no manifest at all.
   * A producer that forgot is #32's shape exactly.
   */
  excludedObjects?: SchemaCompareScopeObjectRef[];
};

/**
 * The comparison's declared scope: what each side was asked about.
 *
 * An object is in the comparison's scope only when it is in scope for BOTH
 * endpoints — the intersection, not the union. That is what makes the
 * narrowed-single-object case (#43) safe: the source was narrowed to one
 * object, the target was not narrowed at all, and every other target object is
 * therefore something the user never asked Jam to look at, let alone drop.
 */
export type SchemaCompareDeclaredScope = {
  source: SchemaCompareEndpointScope;
  target: SchemaCompareEndpointScope;
};

/**
 * Request to run a full schema comparison.
 */
/**
 * Per-endpoint shape inside a RunSchemaCompare request.
 * When `folderSource` is present, the schema fetcher reads the folder instead
 * of the live database. `connectionId` + `database` are still required (used
 * for display + downstream identification — synthetic is fine).
 */
export type RunSchemaCompareEndpoint = {
  connectionId: string;
  database: string;
  /** Single object selection (when comparing a single object instead of entire database) */
  object?: SchemaCompareObjectSelection;
  /** Optional user-defined schema filter (per-endpoint). Ignored when object is set. */
  schemaFilter?: SchemaFilter;
  /** Database Blueprint folder source (optional). */
  folderSource?: { folderPath: string; linkLabel?: string };
};

export type RunSchemaCompareRequest = {
  source: RunSchemaCompareEndpoint;
  target: RunSchemaCompareEndpoint;
  options?: SchemaCompareOptionsDto;
  operationId: string;
  /** Preflight data for main-process license enforcement */
  preflight?: SchemaComparePreflightData;
  /**
   * W5.4 — stable tab identity (distinct from `operationId`, which is fresh
   * per run). When present, a successful run caches its source/target
   * snapshots keyed by this id so a later `schema-compare-rediff` request
   * for the same tab can re-compare without re-fetching. Omit for callers
   * with no tab to key against (e.g. MCP) — they simply don't get cached.
   */
  tabId?: string;
};

/**
 * W5.4 fix round 1 (CRITICAL) — what the renderer currently believes a
 * rediff request's endpoint is. The main process compares this against the
 * cached entry's OWN recorded identity and refuses to serve a rediff on any
 * mismatch, rather than trusting `diffResult` being defined as proof nothing
 * endpoint-related changed. That trust was misplaced: `schemaCompareNavigation.ts`'s
 * `setSchemaCompareSource`/`setSchemaCompareTarget` (wired to the Object
 * Explorer's "Set as Source"/"Set as Target") mutate an existing tab's
 * `content.source`/`content.target` directly and never call `clearResults()`,
 * so a stale `diffResult` from the PREVIOUS pair stays on screen while the
 * tab now names a different database — exactly the class of bug this cache
 * exists to never produce.
 */
export type RediffEndpointIdentity = {
  connectionId: string;
  database: string;
  /** Ignored when `object` is set (mirrors `RunSchemaCompareEndpoint`). */
  schemaFilter?: SchemaFilter;
  /** Single-object selection, when the tab is in single-object mode. */
  object?: SchemaCompareObjectSelection;
};

/**
 * W5.4 — re-run the comparer against the LAST snapshot pair cached for
 * `tabId` (from a prior `schema-compare-run`), under `options`. No new
 * catalog enumeration happens against either endpoint.
 */
export type RediffSchemaCompareRequest = {
  tabId: string;
  /** W5.4 fix round 1 — see `RediffEndpointIdentity`'s doc comment. */
  source: RediffEndpointIdentity;
  target: RediffEndpointIdentity;
  options?: SchemaCompareOptionsDto;
};

/**
 * Result of a rediff attempt. `cacheMiss: true` means the main process
 * either had nothing cached for this tab, `source`/`target` no longer match
 * the cached entry's own identity (fix round 1), or `options` needs data the
 * cached snapshots don't have — the caller should fall back to a full
 * `schema-compare-run`.
 */
export type RediffSchemaCompareResult =
  | { success: true; diffResult: SchemaDiffResult }
  | { success: false; error: string; cacheMiss?: boolean };

/**
 * Which step of a schema-compare run was in flight when it failed. Carried on
 * `RunSchemaCompareResult` so the failure panel can say what was being attempted
 * instead of showing an anonymous error (F12).
 */
export type SchemaComparePhase =
  | 'object-count'
  | 'folder-read'
  | 'schema-fetch'
  | 'compare'
  | 'script-generation';

/**
 * Result from running schema comparison.
 */
export type RunSchemaCompareResult = {
  success: boolean;
  diffResult?: SchemaDiffResult;
  error?: string;
  /** Which endpoint the failure is attributable to, when known. */
  errorSide?: 'source' | 'target';
  /** Which phase of the run failed, when known. */
  errorPhase?: SchemaComparePhase;
  cancelled?: boolean;
  /**
   * Present when `error === 'requires_pro'` and the refusal came from an
   * object-count cap rather than a feature lock (`requireProForLimit` in
   * `electron/licensing/entitlementService.ts`). It is the only part of the
   * envelope that says WHY, so surfaces should prefer it over a generic
   * upgrade sentence.
   */
  limitReason?: string;
  /**
   * S2 — what this run cost, per side and for the comparison itself. Present
   * on a successful run whose two reads both reported a duration; absent
   * otherwise (a failed run, a cancelled one, or a rediff served from the
   * cached snapshot pair, which read no database at all). See
   * `SchemaCompareRunTiming`.
   */
  timing?: SchemaCompareRunTiming;
};

// =============================================================================
// Schema Diff Types
// =============================================================================

/**
 * Change type for a diff entry.
 *
 * `unreadable` means NO VERDICT: at least one side's definition could not be
 * read, so the object was never actually compared. It is deliberately its own
 * member rather than a flag on `modified` — `modified` is scriptable
 * (`useSelectionModel.isScriptableChangeType`) and counted as a real
 * difference everywhere, and neither is true of an object we never read.
 */
export type DiffChangeType = 'added' | 'removed' | 'modified' | 'identical' | 'unreadable';

/**
 * Column-level diff showing what changed in a column.
 */
export type ColumnDiff = {
  name: string;
  changeType: DiffChangeType;
  sourceColumn?: SchemaColumnSnapshot;
  targetColumn?: SchemaColumnSnapshot;
  changes?: ColumnChangeDetail[];
};

/**
 * Detail about what changed in a column.
 */
export type ColumnChangeDetail = {
  property:
    | 'dataType' | 'isNullable' | 'defaultValue' | 'isIdentity'
    | 'maxLength' | 'precision' | 'scale'
    | 'computedExpression' | 'identitySeed' | 'identityIncrement'
    | 'collation' | 'comment' | 'jsonClassification';
  sourceValue: string | number | boolean | null | undefined;
  targetValue: string | number | boolean | null | undefined;
};

/**
 * Property keys that can change at the table-options level.
 */
export type TableOptionPropertyKey =
  // cross-engine
  | 'comment'
  // mssql
  | 'dataCompression' | 'fillFactor' | 'memoryOptimized' | 'durability'
  // mysql
  | 'storageEngine' | 'rowFormat' | 'defaultCharset' | 'defaultCollation' | 'autoIncrement'
  // pgsql (fillFactor shared with mssql key, tablespace shared with oracle)
  | 'tablespace' | 'inheritsFrom' | 'unlogged'
  // oracle
  | 'pctFree' | 'pctUsed' | 'iniTrans' | 'compression' | 'logging' | 'organization'
  // sqlite
  | 'withoutRowid' | 'strict';

/**
 * Detail about what changed in a table-level option.
 */
export type TableOptionChangeDetail = {
  property: TableOptionPropertyKey;
  sourceValue: string | number | boolean | null | undefined;
  targetValue: string | number | boolean | null | undefined;
};

/**
 * Partition-level diff.
 */
export type PartitionDiff = {
  changeType: DiffChangeType;
  sourcePartition?: SchemaPartitionSnapshot;
  targetPartition?: SchemaPartitionSnapshot;
};

/**
 * Constraint-level diff.
 */
export type ConstraintDiff = {
  name: string;
  type: SchemaConstraintSnapshot['type'];
  changeType: DiffChangeType;
  sourceConstraint?: SchemaConstraintSnapshot;
  targetConstraint?: SchemaConstraintSnapshot;
};

/**
 * Index-level diff.
 */
export type IndexDiff = {
  name: string;
  changeType: DiffChangeType;
  sourceIndex?: SchemaIndexSnapshot;
  targetIndex?: SchemaIndexSnapshot;
};

/**
 * Trigger-level diff.
 */
export type TriggerDiff = {
  name: string;
  changeType: DiffChangeType;
  sourceTrigger?: SchemaTriggerSnapshot;
  targetTrigger?: SchemaTriggerSnapshot;
  /** See `RoutineDiff.ownerOnly` (Soporte 9 F1). */
  ownerOnly?: boolean;
};

/**
 * Table-level diff including nested diffs for columns/constraints/indexes.
 */
export type TableDiff = {
  schema: string;
  name: string;
  changeType: DiffChangeType;
  sourceTable?: SchemaTableSnapshot;
  targetTable?: SchemaTableSnapshot;
  columnDiffs: ColumnDiff[];
  constraintDiffs: ConstraintDiff[];
  indexDiffs: IndexDiff[];
  triggerDiffs: TriggerDiff[];
  /** Engine-specific table option diffs (data compression, fill factor, comment, etc.). */
  tableOptionChanges: TableOptionChangeDetail[];
  /** Partition-definition diff. Undefined when neither side is partitioned. */
  partitionDiff?: PartitionDiff;
};

/**
 * View-level diff.
 */
export type ViewDiff = {
  schema: string;
  name: string;
  changeType: DiffChangeType;
  sourceView?: SchemaViewSnapshot;
  targetView?: SchemaViewSnapshot;
  /** See `RoutineDiff.ownerOnly` (Soporte 9 F1). */
  ownerOnly?: boolean;
};

/**
 * Routine-level diff.
 */
export type RoutineDiff = {
  schema: string;
  name: string;
  routineType: 'procedure' | 'function';
  changeType: DiffChangeType;
  sourceRoutine?: SchemaRoutineSnapshot;
  targetRoutine?: SchemaRoutineSnapshot;
  /**
   * Soporte 9 F1 — set (only ever `true`, and only on owner-ignore-capable
   * engines — `ownerIgnoreCapableEngines.ts`) when the two definitions differ
   * solely by the schema qualifier in their CREATE header. On a run WITHOUT
   * `options.ignoreOwner` the diff is `modified` and this tag marks it as
   * reconcilable — the results notice counts these and offers the option
   * where the pain is (the Soporte-8 collation-banner pattern). On a run WITH
   * the option the diff is `identical` and this tag records that the
   * suppression is what reconciled it, so the notice can state the count
   * rather than recurring content-free on every comparison.
   */
  ownerOnly?: boolean;
};

/**
 * Sequence-level diff.
 */
export type SequenceDiff = {
  schema: string;
  name: string;
  changeType: DiffChangeType;
  sourceSequence?: SchemaSequenceSnapshot;
  targetSequence?: SchemaSequenceSnapshot;
};

/**
 * User-defined-type diff (SQL Server only — S7).
 */
export type UserTypeDiff = {
  schema: string;
  name: string;
  changeType: DiffChangeType;
  sourceUserType?: SchemaUserTypeSnapshot;
  targetUserType?: SchemaUserTypeSnapshot;
};

/**
 * Complete schema diff result.
 */
export type SchemaDiffResult = {
  source: {
    connectionId: string;
    database: string;
    /** See `SchemaSnapshot.defaultCollation` (I5 fix-round-1). */
    defaultCollation?: string;
    /** See `SchemaSnapshot.serverVersion` (W1.6). */
    serverVersion?: string;
    /** See `SchemaSnapshot.serverEngineEdition` (S1 1.4.22). */
    serverEngineEdition?: number;
    /**
     * U7 follow-up — this endpoint's OWN `assessCatalogVisibility` answer,
     * copied off its snapshot exactly the way `defaultCollation` and
     * `serverVersion` are, and absent when there was nothing to disclose (see
     * `SchemaSnapshot.catalogVisibility`).
     *
     * Carried per-endpoint because the `catalog-may-be-incomplete` WARNING
     * cannot answer the question the script generator's DROP gate has to ask.
     * That warning names a `side` and carries a count SUMMED across the sides
     * that fired, and the sum is dropped entirely when any firing side was
     * `unverifiable` (`collectCatalogIncompleteWarnings`). So a cross-engine
     * compare whose ORIGIN counted 3 hidden objects and whose destination is a
     * MySQL login with object-by-object grants produces `side: 'both'` with NO
     * count — indistinguishable, from the warning alone, from two uncounted
     * sides. The gate keys on counted evidence about the ORIGIN specifically,
     * which only this field can supply.
     *
     * Swapped with everything else by `reverseDiffResult` for free: it swaps
     * the whole `source`/`target` objects.
     */
    catalogVisibility?: CatalogVisibility;
  };
  target: {
    connectionId: string;
    database: string;
    /** See `SchemaSnapshot.defaultCollation` (I5 fix-round-1). */
    defaultCollation?: string;
    /**
     * See `SchemaSnapshot.serverVersion` (W1.6). This is the value the
     * generator gates `CREATE OR ALTER` on — the sync script executes
     * against the TARGET, not the source.
     */
    serverVersion?: string;
    /**
     * See `SchemaSnapshot.serverEngineEdition` (S1 1.4.22). Read alongside
     * `serverVersion` by the generator's gate: an evergreen Azure edition
     * (5/8/9) overrides a numerically-old version string.
     */
    serverEngineEdition?: number;
    /** See `source.catalogVisibility` above. */
    catalogVisibility?: CatalogVisibility;
  };
  comparedAt: string; // ISO timestamp
  options: SchemaCompareOptionsDto;
  tableDiffs: TableDiff[];
  viewDiffs: ViewDiff[];
  routineDiffs: RoutineDiff[];
  sequenceDiffs?: SequenceDiff[];
  /**
   * S7 — SQL Server user-defined types. Absent (not empty) on a diff where
   * they were not compared: another engine, the type switched off, or either
   * endpoint a folder — see `SchemaSnapshot.userTypes`.
   */
  userTypeDiffs?: UserTypeDiff[];
  summary: DiffSummary;
  /**
   * Every object (both sides) whose definition could not be read. Drives the
   * per-object badge and the count in the results banner. Absent/empty on a
   * comparison where everything was readable.
   */
  unreadable?: SchemaCompareUnreadableObject[];
  /**
   * Every per-table metadata enrichment that failed (both sides), tagged with
   * the endpoint it failed on. Drives the `metadata-enrichment-failed`
   * warning's count (F01/W2.2). Absent/empty on a comparison where every
   * enrichment succeeded.
   */
  enrichmentFailures?: SchemaCompareEnrichmentFailure[];
  /** Non-fatal warnings about this comparison's trustworthiness (I5 fix-round-2, item 4). */
  warnings?: SchemaCompareWarning[];
  /**
   * S1 — what the user asked to compare, composed from both snapshots by
   * `compareSchemas`.
   *
   * Carried on the RESULT, not only on the request, because the surfaces that
   * can destroy something never see the request: the script is generated long
   * after it is gone (renderer → `schema-compare-generate-script`, which takes
   * a renderer-supplied `diffResult` and nothing else), and the MCP path
   * reconstructs its whole selection from `operation.diffResult`.
   *
   * Absent on a diff built before this field existed, or by a caller that
   * assembles a `SchemaDiffResult` by hand — which reads as "unrestricted".
   * See `isObjectInDeclaredScope`.
   */
  declaredScope?: SchemaCompareDeclaredScope;
  /**
   * #121 — source schema name → the name the SAME namespace goes by on the
   * TARGET, for every source schema where the two differ.
   *
   * The sync script is applied to the target, so an ADDED object's `CREATE`
   * must name a schema that exists there. For a MODIFIED object the answer is
   * already in the diff (`targetTable.schema`, #47); for an ADDED one there is
   * no target object to read it from, which is why the comparer — the only
   * layer that holds both endpoints — resolves it here instead.
   *
   * Only entries where source and target names DIFFER are recorded, so the
   * overwhelmingly common same-name compare carries an absent field and every
   * consumer keeps byte-identical behaviour. A schema absent from the map is
   * emitted verbatim: that is the honest default, because a genuinely named
   * schema (`sales`) means the same thing on both sides, and only a schema the
   * comparer FOLDED (an engine default, or #120's endpoint namespace) has a
   * different name over there.
   *
   * Absent on a diff built before this field existed or assembled by hand,
   * which reads as "every schema keeps its name".
   */
  targetSchemaMap?: Readonly<Record<string, string>>;
};

/**
 * Summary counts for the diff result.
 */
export type DiffSummary = {
  tables: DiffCounts;
  views: DiffCounts;
  routines: DiffCounts;
  sequences?: DiffCounts;
  /** S7 — SQL Server user-defined types. */
  userTypes?: DiffCounts;
};

/**
 * Count of changes by type.
 */
export type DiffCounts = {
  added: number;
  removed: number;
  modified: number;
  identical: number;
  /** Objects with no verdict because a definition could not be read (F01). */
  unreadable?: number;
};

// =============================================================================
// DDL Fetching Types (for Diff Viewer)
// =============================================================================

/**
 * Object type for DDL fetching.
 */
export type DdlObjectType = 'table' | 'view' | 'procedure' | 'function' | 'sequence' | 'userType';

/**
 * Request to fetch DDL for an object.
 */
export type FetchObjectDdlRequest = {
  connectionId: string;
  database: string;
  objectType: DdlObjectType;
  schema: string;
  name: string;
  /**
   * If provided AND objectType === 'table', the backend will append CREATE INDEX
   * and ALTER TABLE ADD CONSTRAINT statements for each non-identical entry,
   * picking the source-side or target-side snapshot based on `side`.
   * Identical entries are skipped. Pass `undefined` to get the bare object DDL.
   */
  inlineDependents?: {
    side: 'source' | 'target';
    indexDiffs: IndexDiff[];
    constraintDiffs: ConstraintDiff[];
    triggerDiffs: TriggerDiff[];
  };
};

/**
 * Result from fetching DDL.
 */
export type FetchObjectDdlResult = {
  success: boolean;
  ddl?: string;
  error?: string;
};

// =============================================================================
// Script Generation Types
// =============================================================================

/**
 * Request to generate a synchronization script.
 */
export type GenerateScriptRequest = {
  /** The diff result from comparison */
  diffResult: SchemaDiffResult;
  /** Selected object keys (from selection model) */
  selectedKeys: string[];
  /** Ordered keys for script generation (respecting dependencies) */
  orderedKeys: string[];
  /**
   * Database engine for syntax. The full engine union, not a three-name subset:
   * a MySQL or Oracle tab has always handed this the literal `'mysql'` /
   * `'oracle'` at run time, so the narrower declaration this replaced was
   * already false before anything in this wave touched it.
   */
  engine: DatabaseEngineId;
  /** Wrap statements in transaction */
  wrapInTransaction?: boolean;
  /** Include comments in generated script */
  includeComments?: boolean;
  /**
   * Which side the generated script mutates.
   * - `'source-to-target'` (default): script makes target look like source — the historical behaviour.
   * - `'target-to-source'`: script makes source look like target.
   * Implementation-wise we swap source/target on the diff before generation
   * rather than threading direction through every generator branch.
   */
  direction?: 'source-to-target' | 'target-to-source';
  /**
   * S4 (#31) — the user has explicitly confirmed a script that DROPs every
   * object this comparison examined on the destination, leaving that database
   * with nothing. Set ONLY by the Script Preview dialog's confirmation, after
   * a first generation refused and named the count. See
   * `ScriptGenerationOptions.allowWholeTargetDrop`.
   */
  allowWholeTargetDrop?: boolean;
  /**
   * U7 follow-up — the user has explicitly confirmed the whole-object `DROP`s
   * that Jam withheld because the origin login could not see part of its own
   * catalog. Set ONLY by the Script Preview dialog's second confirmation, after
   * a first generation refused and named the count. See
   * `ScriptGenerationOptions.allowCatalogIncompleteDrops`.
   */
  allowCatalogIncompleteDrops?: boolean;
};

/**
 * What a generated sync script **actually does**, reported by the generator
 * that emitted it rather than predicted by a second implementation.
 *
 * Surfaces that must describe a script before anyone runs it — the Database
 * Blueprint batch "Apply schema to linked DBs…" row is the first — previously
 * re-derived these facts from the diff by hand-copying `planAlterColumn`'s
 * per-engine conditions. Four separate defects came out of that single design
 * choice (an undisclosed default drop on a lenient folder source; two MSSQL
 * shapes where the row promised a script the generator answered with a
 * `-- [REVIEW]` comment; and SQLite, where *every* modified column was falsely
 * "emitting" while the row warned about a default removal SQLite never
 * performs). The cure is structural: the generator counts what it emits, at
 * the point it emits it, so a new emit branch is accounted for by construction.
 *
 * Every number here describes the script as generated for **one specific
 * engine**, with the caller's own options.
 */
export type SyncScriptEmitReport = {
  /** Selected objects that contributed at least one executable statement. */
  emittingObjects: number;
  /**
   * Selected objects that produced only comments (or nothing at all): a
   * `-- [REVIEW]` line for a facet no `ALTER COLUMN` can express, SQLite's
   * "recreate the table" note, a `-- [MANUAL PORT]` cross-engine body, a table
   * that differs only by trigger / table option / partition. Zero
   * `emittingObjects` means the script is comments and nothing else.
   */
  inertObjects: number;
  /**
   * `USE …` + `GO` batches the generator commented out of a module definition
   * it emitted verbatim.
   *
   * The one rewrite the generator performs on a blueprint file's own text, and
   * the reason it has to: a `USE [OtherDb]` batch switches the session, so the
   * `CREATE` half of a DROP+CREATE pair lands in a database the comparison
   * never chose while the `DROP` has already committed against the one it did —
   * measured on a live SQL Server, silently, with `permanentDrops.objects` at
   * `0`. `SET` batches are deliberately left alone: they change how the module
   * compiles and are persisted with it.
   *
   * Non-zero means the generated script differs from the file on disk, so it is
   * disclosed to the user rather than applied quietly. The file itself is never
   * modified.
   */
  neutralizedUseStatements: number;
  /**
   * What the script removes and does **not** put back.
   *
   * Permanent only: the generator also drops a `modified` constraint or index
   * and re-creates it in the same script, and counting those would inflate a
   * warning until nobody reads it. A drop whose re-create is itself only a
   * comment (an unsupported constraint type) *is* permanent and is counted.
   */
  permanentDrops: {
    /**
     * Whole tables / views / routines the script deletes and does not put back.
     *
     * Two producers, not one — reading this as "the `removed` objects" is the
     * mistake that made a real drop invisible for a round. A `removed` object
     * yields `DROP TABLE/VIEW/…`, and a **`modified`** view or routine yields
     * one too whenever the source definition cannot be turned into a `CREATE`
     * (an empty blueprint file, or one holding SSMS's `ALTER …` output):
     * `generateAlterView` / `generateAlterRoutine` fall back to DROP + CREATE
     * and the CREATE half is then only a comment.
     */
    objects: number;
    columns: number;
    constraints: number;
    indexes: number;
    /** Column defaults dropped with no re-add — they arrive via column diffs. */
    defaults: number;
  };
  /**
   * S1 — whole objects whose `DROP` this run REFUSED to emit because the
   * object is outside the comparison's declared scope
   * (`SchemaDiffResult.declaredScope`).
   *
   * A count, not a silence. The destructive statement is withheld AND said
   * out loud: a `-- [REFUSED]` block in the script itself, a `warnings[]`
   * entry, and this number for surfaces that describe a script before anyone
   * reads it. A silently shrinking script is how this class of defect hides —
   * #43 and #30 would both *look* fixed with no evidence left when they are
   * not.
   *
   * Required, not optional, so a future `SyncScriptEmitReport` producer that
   * forgets it is a compile error rather than a surface that reports `0`
   * refusals it never counted.
   */
  refusedOutOfScope: number;
  /**
   * S4 (#31) — whole objects whose `DROP` this run REFUSED to emit because the
   * script would have dropped EVERY object the comparison examined on the
   * target, leaving that database with nothing.
   *
   * Distinct from `refusedOutOfScope`, and deliberately so: that one is
   * "you never asked about this object", this one is "you asked about all of
   * them and the answer is a wholesale wipe". In the reported case (#31) the
   * declared scope is legitimately the whole database, so the S1 refusal
   * correctly permits it and this count is the only thing standing between the
   * user and a whole-schema DROP.
   *
   * Non-zero means the destructive statements were WITHHELD and said out loud
   * (a `-- [REFUSED]` block, a `warnings[]` entry, this number). Zero after an
   * explicit `allowWholeTargetDrop` confirmation — the script then carries a
   * disclosure warning instead, because a confirmed wipe is still a wipe. See
   * `utils/wholeTargetDrop.ts`.
   *
   * Required for the same reason as `refusedOutOfScope`.
   */
  refusedWholeTargetDrop: number;
  /**
   * U7 follow-up — whole objects whose `DROP` this run REFUSED to emit because
   * the ORIGIN's login demonstrably could not see part of its own catalog, so
   * an object absent there is indistinguishable from one that was deleted.
   *
   * The third refusal, and the only one keyed on EVIDENCE ABOUT THE READER
   * rather than on the shape of the script: `refusedOutOfScope` is "you never
   * asked about this object", `refusedWholeTargetDrop` is "this would empty the
   * database", this one is "the catalog this verdict was computed from is
   * provably short, so the verdict itself may be wrong".
   *
   * Fires ONLY on counted evidence — `assessCatalogVisibility` answering
   * `{ kind: 'objects-hidden', count }` on the origin (MSSQL's DENY rows,
   * Oracle's `DBA_OBJECTS` − `ALL_OBJECTS`). `{ kind: 'unverifiable' }` keeps
   * #93's disclosure-only behaviour and is deliberately NOT gated — see
   * `refuseWholeObjectDrop`'s catalog branch in `schemaScriptGenerator.ts` for
   * why a guard the login can never satisfy is worse than a disclosure.
   *
   * There is NO floor here, unlike `WHOLE_TARGET_DROP_FLOOR`'s five: that floor
   * exists because below five a user can read every DROP in the preview and
   * judge it, and reading a DROP cannot judge it against an object the login
   * cannot see.
   *
   * Non-zero means the destructive statements were WITHHELD and said out loud
   * (a `-- [REFUSED]` line NAMING each object, a `warnings[]` entry, this
   * number). Zero after an explicit `allowCatalogIncompleteDrops` confirmation
   * — the script then carries #93's disclosure warning instead.
   *
   * Required for the same reason as `refusedOutOfScope`.
   */
  refusedCatalogIncomplete: number;
};

/**
 * Result from script generation.
 */
export type GenerateScriptResult = {
  success: boolean;
  script?: string;
  error?: string;
  /** Warnings about unsupported operations */
  warnings?: string[];
  /**
   * What the emitted script does, counted by the generator itself. Absent when
   * generation failed (there is no script to describe).
   */
  emitReport?: SyncScriptEmitReport;
};

// =============================================================================
// Schema Object Count Types (for License Gating)
// =============================================================================

/**
 * Request to get schema object counts (for preflight license check).
 */
export type GetSchemaObjectCountsRequest = {
  connectionId: string;
  database: string;
  /** Filter options (affects which object types are counted) */
  options?: SchemaCompareOptionsDto;
  /** Optional user-defined schema filter (per-endpoint). */
  schemaFilter?: SchemaFilter;
};

/**
 * Object counts for a database schema.
 */
export type SchemaObjectCounts = {
  tables: number;
  views: number;
  procedures: number;
  functions: number;
  /**
   * Sequences the compare will diff. Counted from W4.3/W4.4 onward, because
   * `compareSchemas` has always produced `sequenceDiffs` while no cap probe
   * counted them — inert while Oracle alone enumerated sequences, a licensing
   * hole the moment SQL Server and PostgreSQL do too. Optional so a snapshot
   * persisted by an older build still reads.
   */
  sequences?: number;
  /** S7 — SQL Server user-defined types the compare will diff. */
  userTypes?: number;
};

/**
 * Result from getting schema object counts.
 */
export type GetSchemaObjectCountsResult = {
  success: boolean;
  counts?: SchemaObjectCounts;
  error?: string;
};

// =============================================================================
// Comparison Options File (W5.1b — export/load Setup dialog options as a
// shareable file). The main process only ever moves BYTES here — it is
// handed a finished JSON document to write, and hands one back on read.
// Assembling and validating the document (never carrying connection/database
// identity) is the renderer's job — see
// `schema-compare/utils/compareOptionsFile.ts`. Pattern mirrors
// `settingsTransferIpc.ts` / `SettingsTransferExportRequest`.
// =============================================================================

export type SchemaCompareOptionsFileExportRequest = {
  /** The complete, already-serialized options document. */
  json: string;
  suggestedFileName?: string;
  /** Test seam: skip the native save dialog. */
  filePath?: string;
};

export type SchemaCompareOptionsFileExportResult =
  | { success: true; filePath: string }
  | { success: false; cancelled?: boolean; error?: string };

export type SchemaCompareOptionsFileImportRequest = {
  /** Test seam: skip the native open dialog. */
  filePath?: string;
};

export type SchemaCompareOptionsFileImportResult =
  | { success: true; json: string; filePath: string }
  | { success: false; cancelled?: boolean; error?: string };
